import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { NO_CACHE_HEADERS, normalizeEliminado } from '../api';
import CredentialDeliveryTasks from '../components/CredentialDeliveryTasks';

// --- HELPERS EXTERNOS Y CONSTANTES ---
const EMPTY_ARRAY = [];
const LISTA_PLATAFORMAS = ['Netflix', 'Hbo Max', 'Disney+', 'Prime Video', 'Spotify', 'Crunchyroll', 'Paramount', 'GPT', 'Otros'];
const normalizarServicio = (valor) => {
  const servicio = String(valor || '').trim();
  return /^prime(?:\s+video)?$/i.test(servicio) ? 'Prime Video' : servicio;
};

const calcularSiguienteVencimiento = (fechaBaseStr, dias = 30) => {
  if (!fechaBaseStr) return '';
  
  // Normalizar fecha formateada o timestamp a YYYY-MM-DD
  const fechaLimpia = String(fechaBaseStr).split('T')[0];
  const [year, month, day] = fechaLimpia.split('-').map(Number);
  
  if (!year || !month || !day) return '';

  // Crear fecha en zona horaria local
  const fecha = new Date(year, month - 1, day);
  fecha.setDate(fecha.getDate() + dias);

  const yyyy = fecha.getFullYear();
  const mm = String(fecha.getMonth() + 1).padStart(2, '0');
  const dd = String(fecha.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const MESES_ES = ['Ene.', 'Feb.', 'Mar.', 'Abr.', 'May.', 'Jun.', 'Jul.', 'Ago.', 'Set.', 'Oct.', 'Nov.', 'Dic.'];

const formatearFechaLegible = (fechaStr) => {
  if (!fechaStr) return '-';
  const limpia = String(fechaStr).split('T')[0].split(' ')[0];
  const partes = limpia.split('-');
  if (partes.length === 3 && partes[0] && partes[1] && partes[2]) {
    const yyyy = partes[0];
    const mmIndex = parseInt(partes[1], 10) - 1;
    const dd = parseInt(partes[2], 10);
    const mes = MESES_ES[mmIndex] || partes[1];
    return `${dd} ${mes} ${yyyy}`;
  }
  return fechaStr;
};

const sumarDiasFechaCalendario = (fechaStr, dias) => {
  if (!fechaStr) return '';
  const limpia = String(fechaStr).split('T')[0].split(' ')[0];
  const partes = limpia.split('-');
  if (partes.length !== 3) return '';
  const y = parseInt(partes[0], 10);
  const m = parseInt(partes[1], 10);
  const d = parseInt(partes[2], 10);
  if (isNaN(y) || isNaN(m) || isNaN(d)) return '';
  const numDias = parseInt(dias, 10) || 0;
  
  const utcDate = new Date(Date.UTC(y, m - 1, d + numDias));
  const resY = utcDate.getUTCFullYear();
  const resM = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
  const resD = String(utcDate.getUTCDate()).padStart(2, '0');
  return `${resY}-${resM}-${resD}`;
};

const sumarDiasFechaLocal = calcularSiguienteVencimiento;

const formatearFechaDDMM = (fechaStr) => {
  if (!fechaStr) return '-';
  const str = String(fechaStr).trim();
  if (!str) return '-';
  const fechaLimpia = str.split('T')[0].split(' ')[0];
  const partes = fechaLimpia.split('-');
  if (partes.length === 3 && partes[0] && partes[1] && partes[2]) {
    return `${partes[2].padStart(2, '0')}/${partes[1].padStart(2, '0')}`;
  }
  if (dayjs(str).isValid()) {
    return dayjs(str).add(10, 'hour').format('DD/MM');
  }
  return str;
};

const getFechaVencimientoStr = (c) => {
  if (!c) return '';
  return c.fecha_finalizacion || c.fecha_fin || c.vencimiento || c.fechaVencimiento || c.vence || c.end_date || c.fechaVence || '';
};

const compararVencimiento = (a, b) => {
  const fA = getFechaVencimientoStr(a);
  const fB = getFechaVencimientoStr(b);

  if (!fA && !fB) return 0;
  if (!fA) return 1;
  if (!fB) return -1;

  return dayjs(fA).valueOf() - dayjs(fB).valueOf();
};

function VistaStreaming({ api }) {
  // --- DATOS ---
  const [dataClientes, setDataClientes] = useState([]);
  const [dataEliminados, setDataEliminados] = useState([]);
  const [dataInventario, setDataInventario] = useState([]);
  const [serviciosPersonalizados, setServiciosPersonalizados] = useState([]);
  const [credencialesReveladas, setCredencialesReveladas] = useState({});
  const [pinsRevelados, setPinsRevelados] = useState({});

  // --- ESTADO NOTIFICACIONES Y PAPELERA ---
  const [mostrarNotificaciones, setMostrarNotificaciones] = useState(false);
  const [selectedExpirationIds, setSelectedExpirationIds] = useState([]);
  const [isBulkDeletingExpiration, setIsBulkDeletingExpiration] = useState(false);
  const [mostrarPapelera, setMostrarPapelera] = useState(false);
  const [filtroPapelera, setFiltroPapelera] = useState('');
  const [selectedTrashIds, setSelectedTrashIds] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [toast, setToast] = useState({ mostrar: false, mensaje: '', tipo: 'success' });
  const [modalEnviarCuenta, setModalEnviarCuenta] = useState(null);
  const [modalActualizacionClave, setModalActualizacionClave] = useState(null);
  const [modalRecordatorioRenovacion, setModalRecordatorioRenovacion] = useState(null);
  const [recordatorioCopiadoId, setRecordatorioCopiadoId] = useState(null);

  // --- FILTROS ---
  const [filtroCliente, setFiltroCliente] = useState('Todos');
  const [dropdownAbierto, setDropdownAbierto] = useState(false);
  const dropdownRef = useRef(null);
  const [filtroStock, setFiltroStock] = useState('Todos');
  const [busquedaCliente, setBusquedaCliente] = useState('');

  // --- ESTADOS EDICIÓN ---
  const [editandoCliente, setEditandoCliente] = useState(false);
  const [clienteEditarId, setClienteEditarId] = useState(null);
  const [cargandoGuardar, setCargandoGuardar] = useState(false);
  const [editandoStock, setEditandoStock] = useState(false);
  const [stockEditarId, setStockEditarId] = useState(null);

  // --- ESTADO RENOVACIÓN PERFIL ---
  const [perfilRenovar, setPerfilRenovar] = useState(null);
  const [formRenovar, setFormRenovar] = useState({
    id: null,
    nombre_cliente: '',
    servicio: 'Netflix',
    perfil: '',
    pin: '',
    monto: '',
    fecha_inicio: '',
    fecha_finalizacion: ''
  });
  const [cargandoRenovacion, setCargandoRenovacion] = useState(false);

  // --- ESTADO RENOVACIÓN DE STOCK ---
  const [stockRenovar, setStockRenovar] = useState(null);
  const [formRenovarStock, setFormRenovarStock] = useState({
    id: null,
    correo: '',
    servicio: 'Netflix',
    contrasena: '',
    monto: '',
    fechaInicio: dayjs().format('YYYY-MM-DD'),
    nuevaFechaVence: '',
    actualizarCostoBase: false
  });
  const [cargandoRenovacionStock, setCargandoRenovacionStock] = useState(false);

  // --- FORMULARIOS ---
  const [formCliente, setFormCliente] = useState({ 
    nombre: '', celular: '', servicio: 'Netflix', perfil: '', pin: '', 
    correo: '', contrasena: '', 
    fecha_inicio: dayjs().format('YYYY-MM-DD'), 
    fecha_fin: dayjs().add(30, 'day').format('YYYY-MM-DD'),
    monto: '',
    account_id: null
  });
  const [diasExtras, setDiasExtras] = useState(0);
  const [comentarioAjuste, setComentarioAjuste] = useState('');

  const [formStock, setFormStock] = useState({ 
    correo: '', contrasena: '', servicio: 'Netflix', costo: '', 
    fecha_entrada: dayjs().format('YYYY-MM-DD'), fecha_vencimiento: dayjs().add(30, 'day').format('YYYY-MM-DD'),
    mode: 'new', sourceAccountId: '', reason: '', note: ''
  });
  const replacementAttemptKeyRef = useRef('');
  const credentialAttemptKeyRef = useRef('');
  const externalCredentialAttemptRef = useRef(null);
  const [replacementPreview, setReplacementPreview] = useState(null);
  const [credentialTasksVersion, setCredentialTasksVersion] = useState(0);

  // --- CARGAR DATOS ---
  const cargarDatos = useCallback(() => {
    const ts = `${Date.now()}_${Math.floor(Math.random() * 1000000)}`; 
    const headers = NO_CACHE_HEADERS;

    axios.get(`${api}/clientes?t=${ts}`, { headers }).then(res => {
      const list = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
      const normalized = normalizeEliminado(list);
      setDataClientes(normalized);
    }).catch(err => {
      console.error("Error al cargar clientes:", err);
      setDataClientes([]);
    });

    axios.get(`${api}/clientes/eliminados?t=${ts}`, { headers }).then(res => {
      const list = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
      const normalized = normalizeEliminado(list);
      setDataEliminados(normalized);
    }).catch(err => {
      setDataEliminados([]);
    });

    axios.get(`${api}/inventario?t=${ts}`, { headers }).then(res => {
      const list = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
      setDataInventario(list);
    }).catch(err => {
      console.error("Error al cargar inventario:", err);
      setDataInventario([]);
    });

    axios.get(`${api}/servicios-personalizados?t=${ts}`, { headers }).then(res => {
      setServiciosPersonalizados(Array.isArray(res.data) ? res.data : []);
    }).catch(err => {
      console.error('Error al cargar servicios personalizados:', err);
      setServiciosPersonalizados([]);
    });
  }, [api]); 

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (dropdownAbierto) setDropdownAbierto(false);
        if (perfilRenovar) setPerfilRenovar(null);
        if (stockRenovar) setStockRenovar(null);
        if (mostrarPapelera) {
          setMostrarPapelera(false);
          setSelectedTrashIds([]);
        }
        if (mostrarNotificaciones) {
          setMostrarNotificaciones(false);
          setSelectedExpirationIds([]);
        }
        if (modalEnviarCuenta) setModalEnviarCuenta(null);
        if (modalActualizacionClave) setModalActualizacionClave(null);
        if (modalRecordatorioRenovacion) setModalRecordatorioRenovacion(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dropdownAbierto, perfilRenovar, stockRenovar, mostrarPapelera, mostrarNotificaciones, modalEnviarCuenta, modalActualizacionClave, modalRecordatorioRenovacion]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownAbierto(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const credencialesCargadasRef = useRef(new Set());
  useEffect(() => {
    if (Array.isArray(dataInventario) && dataInventario.length > 0) {
      dataInventario.forEach(acc => {
        if (acc && acc.id && !credencialesCargadasRef.current.has(acc.id)) {
          credencialesCargadasRef.current.add(acc.id);
          axios.get(`${api}/inventario/${acc.id}/credencial`, { headers: NO_CACHE_HEADERS })
            .then(res => {
              if (res.data && res.data.secret) {
                setCredencialesReveladas(prev => ({ ...prev, [acc.id]: res.data.secret }));
              }
            })
            .catch(() => {});
        }
      });
    }
  }, [dataInventario, api]);

  const revelarCredencial = useCallback(async (cuentaId) => {
    if (!cuentaId) return;
    try {
      const { data } = await axios.get(`${api}/inventario/${cuentaId}/credencial`, { headers: NO_CACHE_HEADERS });
      setCredencialesReveladas(prev => ({ ...prev, [cuentaId]: data.secret }));
      window.setTimeout(() => {
        setCredencialesReveladas(prev => {
          const siguiente = { ...prev };
          delete siguiente[cuentaId];
          return siguiente;
        });
      }, Number(data.expiresInSeconds || 15) * 1000);
    } catch (_) {
      mostrarToast('No se pudo revelar la credencial.', 'danger');
    }
  }, [api]);

  const revelarPin = useCallback(async (suscripcionId) => {
    if (!suscripcionId) return;
    try {
      const { data } = await axios.get(`${api}/clientes/${suscripcionId}/pin`, { headers: NO_CACHE_HEADERS });
      setPinsRevelados(prev => ({ ...prev, [suscripcionId]: data.pin }));
      window.setTimeout(() => {
        setPinsRevelados(prev => {
          const siguiente = { ...prev };
          delete siguiente[suscripcionId];
          return siguiente;
        });
      }, Number(data.expiresInSeconds || 15) * 1000);
    } catch (_) {
      mostrarToast('No se pudo revelar el PIN.', 'danger');
    }
  }, [api]);

  // --- CÁLCULOS SEGUROS MEMOIZADOS ---
  const enPapelera = useMemo(() => {
    const directos = Array.isArray(dataEliminados) ? dataEliminados : [];
    const desdeClientes = Array.isArray(dataClientes) ? dataClientes.filter(p => p.eliminado === 1 || p.eliminado === true || p.eliminado === '1' || p.eliminado === 'true' || Number(p.eliminado) === 1) : [];

    const mapaPapelera = new Map();
    [...directos, ...desdeClientes].forEach(p => {
      if (p && p.id != null) {
        mapaPapelera.set(p.id, {
          ...p,
          eliminado: 1
        });
      }
    });

    return Array.from(mapaPapelera.values());
  }, [dataEliminados, dataClientes]);

  const safeClientes = useMemo(() => {
    if (!Array.isArray(dataClientes)) return EMPTY_ARRAY;
    return dataClientes.filter(c => !(c.eliminado === 1 || c.eliminado === true || c.eliminado === '1' || c.eliminado === 'true' || Number(c.eliminado) === 1));
  }, [dataClientes]);

  const safeInventario = useMemo(() => (Array.isArray(dataInventario) ? dataInventario : EMPTY_ARRAY), [dataInventario]);
  const safeEliminados = enPapelera;

  const perfilesPapeleraFiltrados = useMemo(() => {
    const f = filtroPapelera.toLowerCase().trim();
    if (!f) return safeEliminados;
    return safeEliminados.filter(p => {
      return (p.nombre_cliente || p.nombre || '').toLowerCase().includes(f) ||
        (p.correo || p.email || '').toLowerCase().includes(f) ||
        (p.servicio || '').toLowerCase().includes(f) ||
        (p.perfil || '').toLowerCase().includes(f) ||
        (p.numero_celular || p.celular || '').toLowerCase().includes(f);
    });
  }, [safeEliminados, filtroPapelera]);

  const idsPapeleraFiltrados = useMemo(() => perfilesPapeleraFiltrados.map(p => p.id).filter(Boolean), [perfilesPapeleraFiltrados]);
  const todosPapeleraSeleccionados = idsPapeleraFiltrados.length > 0 && idsPapeleraFiltrados.every(id => selectedTrashIds.includes(id));
  const algunosPapeleraSeleccionados = idsPapeleraFiltrados.some(id => selectedTrashIds.includes(id)) && !todosPapeleraSeleccionados;

  const toggleSeleccionarPerfilPapelera = (id) => {
    setSelectedTrashIds(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const toggleSeleccionarTodosPapelera = () => {
    if (todosPapeleraSeleccionados) {
      setSelectedTrashIds(prev => prev.filter(id => !idsPapeleraFiltrados.includes(id)));
    } else {
      const nuevoSet = new Set([...selectedTrashIds, ...idsPapeleraFiltrados]);
      setSelectedTrashIds(Array.from(nuevoSet));
    }
  };

  const listaServiciosDisponibles = useMemo(() => {
    const nombres = [
      ...LISTA_PLATAFORMAS,
      ...serviciosPersonalizados,
      ...safeInventario.map(cuenta => cuenta.service || cuenta.servicio || cuenta.platform),
      ...safeClientes.map(cliente => cliente.service || cliente.servicio || cliente.platform || cliente?.streaming_accounts?.service)
    ];
    const serviciosUnicos = new Map();
    nombres.forEach(nombre => {
      if (typeof nombre !== 'string' || !nombre.trim()) return;
      const servicio = normalizarServicio(nombre);
      const clave = servicio.toLowerCase();
      if (!serviciosUnicos.has(clave)) serviciosUnicos.set(clave, servicio);
    });
    return Array.from(serviciosUnicos.values());
  }, [serviciosPersonalizados, safeInventario, safeClientes]);

  const totalIngresosClientes = safeClientes.reduce((acc, cliente) => acc + Number(cliente.monto || 0), 0);
  const inversionRealStock = safeInventario.reduce((acc, cuenta) => acc + Number(cuenta.costo || 0), 0);
  const gananciaNetaClientes = totalIngresosClientes - inversionRealStock;

  const reportePlataformas = safeClientes.reduce((acc, curr) => {
    const rawPlat = normalizarServicio(curr.service || curr.servicio || curr.platform || 'Otros');
    const plat = listaServiciosDisponibles.find(p => p.toLowerCase() === rawPlat.toLowerCase()) || rawPlat;
    if (!acc[plat]) acc[plat] = { cantidad: 0, dinero: 0 };
    acc[plat].cantidad += 1;
    acc[plat].dinero += Number(curr.monto || 0);
    return acc;
  }, {});

  const conteoStock = safeInventario.reduce((acc, curr) => {
    if (curr.status === 'failed') return acc;
    const rawPlat = normalizarServicio(curr.service || curr.servicio || curr.platform || '');
    const plat = listaServiciosDisponibles.find(p => p.toLowerCase() === rawPlat.toLowerCase()) || rawPlat;
    acc[plat] = (acc[plat] || 0) + 1;
    return acc;
  }, {});

  // --- LÓGICA DE NOTIFICACIONES ---
  const clientesPorVencer = useMemo(() => {
    const hoy = dayjs();
    return safeClientes.filter(c => {
        const fechaStr = getFechaVencimientoStr(c);
        if (!fechaStr) return false;
        const fechaFin = dayjs(fechaStr);
        const diasRestantes = fechaFin.diff(hoy, 'day');
        return diasRestantes <= 3;
    }).sort(compararVencimiento);
  }, [safeClientes]);

  // --- LÓGICA DE SELECCIÓN EN VENCIMIENTOS PRÓXIMOS ---
  const idsVencimientos = useMemo(() => clientesPorVencer.map(c => c.id).filter(Boolean), [clientesPorVencer]);
  const todosVencimientosSeleccionados = idsVencimientos.length > 0 && idsVencimientos.every(id => selectedExpirationIds.includes(id));
  const algunosVencimientosSeleccionados = idsVencimientos.some(id => selectedExpirationIds.includes(id)) && !todosVencimientosSeleccionados;

  const toggleSeleccionarPerfilVencimiento = (id) => {
    setSelectedExpirationIds(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const toggleSeleccionarTodosVencimientos = () => {
    if (todosVencimientosSeleccionados) {
      setSelectedExpirationIds(prev => prev.filter(id => !idsVencimientos.includes(id)));
    } else {
      const nuevoSet = new Set([...selectedExpirationIds, ...idsVencimientos]);
      setSelectedExpirationIds(Array.from(nuevoSet));
    }
  };

  // FILTRADO MEMOIZADO
  const clientesFiltrados = useMemo(() => {
    const esFiltroTodas = !filtroCliente || 
                          filtroCliente.trim() === '' || 
                          filtroCliente.toLowerCase() === 'todas' || 
                          filtroCliente.toLowerCase() === 'todas las apps' || 
                          filtroCliente.toLowerCase() === 'all' ||
                          filtroCliente.toLowerCase() === 'todos';

    return (safeClientes || []).filter(cuenta => {
      const nombreServicio = (
        cuenta.service || 
        cuenta.servicio || 
        cuenta.platform || 
        cuenta?.streaming_accounts?.service || 
        ''
      ).toString().toLowerCase().trim();

      const coincideApp = esFiltroTodas || nombreServicio === (filtroCliente || '').toLowerCase().trim();

      const busqueda = (busquedaCliente || '').toLowerCase().trim();
      const perfilesLista = Array.isArray(cuenta.perfiles) 
        ? cuenta.perfiles 
        : (Array.isArray(cuenta.subscriptions) ? cuenta.subscriptions : (Array.isArray(cuenta.profiles) ? cuenta.profiles : []));

      const coincideBusqueda = !busqueda || 
        (cuenta.email || cuenta.correo || '').toLowerCase().includes(busqueda) ||
        nombreServicio.includes(busqueda) ||
        (cuenta.nombre_cliente || cuenta.nombre || '').toLowerCase().includes(busqueda) ||
        (cuenta.numero_celular || cuenta.celular || '').toLowerCase().includes(busqueda) ||
        (cuenta.perfil || '').toLowerCase().includes(busqueda) ||
        perfilesLista.some(p => 
          (p.nombre || p.cliente || p.profile_label || p.full_name || p.nombre_cliente || '').toLowerCase().includes(busqueda)
        );

      return coincideApp && coincideBusqueda;
    });
  }, [safeClientes, filtroCliente, busquedaCliente]);

  const stockFiltrado = useMemo(() => {
    return safeInventario.filter(i => {
      const selected = (filtroStock || '').toString().trim().toLowerCase();
      const isFailed = String(i.status || 'active').toLowerCase() === 'failed';
      if (selected === 'caidas') return isFailed;
      if (isFailed) return false;
      if (!selected || selected === 'todos' || selected === 'todas') return true;
      return normalizarServicio(i.service || i.servicio || i.platform).toLowerCase() === normalizarServicio(selected).toLowerCase();
    });
  }, [safeInventario, filtroStock]);

  // AGRUPACIÓN Y ORDENAMIENTO DEUDORES/VENCIDOS PRIMERO
  const clientesAgrupados = useMemo(() => {
    const grupos = {};
    const clientesOrdenados = [...clientesFiltrados].sort(compararVencimiento);

    clientesOrdenados.forEach(c => {
      const rawServicio = normalizarServicio(c.service || c.servicio || c.platform || c?.streaming_accounts?.service || 'Otros');
      const servicio = listaServiciosDisponibles.find(p => p.toLowerCase() === rawServicio.toLowerCase()) || rawServicio;
      const correo = c.correo || c.email || 'Sin Correo Asignado';
      if (!grupos[servicio]) grupos[servicio] = {};
      if (!grupos[servicio][correo]) grupos[servicio][correo] = [];
      grupos[servicio][correo].push(c);
    });

    return grupos;
  }, [clientesFiltrados, listaServiciosDisponibles]);

  // COLORES MARCA
  const getBrandColor = (servicio) => {
    const s = (servicio || '').toLowerCase();
    if(s.includes('netflix')) return '#E50914';
    if(s.includes('disney')) return '#113CCF';
    if(s.includes('hbo max') || s.includes('hbo')) return '#991EEB';
    if(s.includes('prime video')) return '#00A8E1';
    if(s.includes('spotify')) return '#1DB954';
    if(s.includes('crunchyroll')) return '#F47521';
    return '#343a40';
  };

  // --- TOAST HELPER ---
  const mostrarToast = (mensaje, tipo = 'success') => {
    setToast({ mostrar: true, mensaje, tipo });
    setTimeout(() => setToast({ mostrar: false, mensaje: '', tipo: 'success' }), 4000);
  };

  const crearServicioPersonalizado = async (destino) => {
    const valorIngresado = window.prompt('Nombre del nuevo servicio (ejemplo: Canva o CapCut):');
    if (valorIngresado === null) return;

    const nombre = valorIngresado.trim().replace(/\s+/g, ' ');
    if (!nombre) {
      mostrarToast('Escribe un nombre para el servicio.', 'warning');
      return;
    }

    const servicioExistente = listaServiciosDisponibles.find(servicio => servicio.toLowerCase() === nombre.toLowerCase());
    const servicio = servicioExistente || nombre;
    if (!servicioExistente) {
      try {
        const { data } = await axios.post(`${api}/servicios-personalizados`, { nombre: servicio });
        const nombreGuardado = data?.nombre || servicio;
        setServiciosPersonalizados(actuales => [...actuales, nombreGuardado]);
        if (destino === 'cliente') setFormCliente(actual => ({ ...actual, servicio: nombreGuardado }));
        if (destino === 'stock') setFormStock(actual => ({ ...actual, servicio: nombreGuardado }));
        mostrarToast(`Servicio ${nombreGuardado} agregado.`, 'success');
      } catch (error) {
        mostrarToast(`No se pudo guardar el servicio: ${error.response?.data?.error || error.message}`, 'danger');
      }
      return;
    }

    if (destino === 'cliente') setFormCliente(actual => ({ ...actual, servicio }));
    if (destino === 'stock') setFormStock(actual => ({ ...actual, servicio }));
    mostrarToast(`El servicio ${servicio} ya existe.`, 'warning');
  };

  const eliminarServicioPersonalizado = async (nombreSeleccionado) => {
    const nombre = serviciosPersonalizados.find(servicio => servicio.toLowerCase() === (nombreSeleccionado || '').toLowerCase());
    if (!nombre) {
      mostrarToast('Selecciona un servicio personalizado para eliminar.', 'warning');
      return;
    }
    if (!window.confirm(`¿Eliminar el servicio ${nombre}?`)) return;
    try {
      await axios.delete(`${api}/servicios-personalizados`, { data: { nombre } });
      setServiciosPersonalizados(actuales => actuales.filter(servicio => servicio.toLowerCase() !== nombre.toLowerCase()));
      setFormCliente(actual => actual.servicio.toLowerCase() === nombre.toLowerCase() ? { ...actual, servicio: 'Otros' } : actual);
      setFormStock(actual => actual.servicio.toLowerCase() === nombre.toLowerCase() ? { ...actual, servicio: 'Netflix' } : actual);
      mostrarToast(`Servicio ${nombre} eliminado.`, 'success');
    } catch (error) {
      mostrarToast(error.response?.data?.error || `No se pudo eliminar el servicio: ${error.message}`, 'danger');
    }
  };

  // --- HANDLERS ELIMINAR / RESTAURAR ---
  const handleWheel = (e) => e.target.blur();

  const handleEliminar = (target, tipo) => { 
    if (tipo === 'cliente') {
      const clienteObj = typeof target === 'object' ? target : safeClientes.find(c => c.id === target);
      const id = typeof target === 'object' ? target.id : target;
      const nombreCliente = clienteObj ? (clienteObj.nombre_cliente || clienteObj.nombre || '>>> LIBRE <<<') : 'Perfil';
      const servicio = clienteObj?.servicio || '';
      const perfil = clienteObj?.perfil || '';

      if (!id) {
        mostrarToast("ID de perfil no encontrado", "danger");
        return;
      }

      const mensajeConfirmacion = [
        `¿Mover el perfil de "${nombreCliente}" a la Papelera?`,
        servicio || perfil ? `\n\nCliente: ${nombreCliente}\nServicio: ${servicio || 'N/A'}\nPerfil: ${perfil || 'N/A'}` : '',
        `\n\nEl perfil será enviado a la Papelera y podrá restaurarse posteriormente.`
      ].filter(Boolean).join('');

      if (!window.confirm(mensajeConfirmacion)) return; 

      setSelectedExpirationIds(prev => prev.filter(item => item !== id));
      const perfilEliminado = clienteObj ? { ...clienteObj, eliminado: 1, fecha_eliminacion: new Date().toISOString() } : null;
      setDataClientes(prev => (Array.isArray(prev) ? prev.filter(c => c.id !== id) : []));
      if (perfilEliminado) {
        setDataEliminados(prev => (Array.isArray(prev) ? [perfilEliminado, ...prev] : [perfilEliminado]));
      }

      axios.delete(`${api}/delete/${id}`).then(res => {
        if (res.data && res.data.success === false) {
          throw new Error(res.data.error || "Error al mover a la papelera");
        }
        mostrarToast(`Perfil de "${nombreCliente}" movido a la Papelera 🗑️`, 'warning');
        cargarDatos();
      }).catch(err => {
        console.error("Error al mover a la papelera:", err);
        mostrarToast("Error al mover perfil a la papelera", "danger");
        cargarDatos();
      });
    } else {
      const id = typeof target === 'object' ? target.id : target;
      if (!id) return;
      if (!window.confirm("¿Eliminar esta cuenta del stock?")) return; 

      setDataInventario(prev => (Array.isArray(prev) ? prev.filter(i => i.id !== id) : []));

      axios.delete(`${api}/inventario/${id}`).then(res => {
        if (res.data && res.data.success === false) {
          throw new Error(res.data.error || "Error al eliminar del stock");
        }
        mostrarToast("Cuenta eliminada del stock", "warning");
        cargarDatos();
      }).catch(err => {
        console.error("Error al eliminar stock:", err);
        mostrarToast("Error al eliminar cuenta del stock", "danger");
        cargarDatos();
      }); 
    }
  };

  const handleRestaurar = (id, nombre) => {
    if (!id) {
      mostrarToast("ID de perfil no encontrado", "danger");
      return;
    }

    setSelectedTrashIds(prev => prev.filter(item => item !== id));
    const perfilRestaurado = safeEliminados.find(item => item.id === id);
    setDataEliminados(prev => (Array.isArray(prev) ? prev.filter(item => item.id !== id) : []));
    if (perfilRestaurado) {
      setDataClientes(prev => (Array.isArray(prev) ? [...prev, { ...perfilRestaurado, eliminado: 0, fecha_eliminacion: null }] : [{ ...perfilRestaurado, eliminado: 0, fecha_eliminacion: null }]));
    }

    axios.put(`${api}/clientes/restaurar/${id}`).then(res => {
      if (res.data && res.data.success === false) {
        throw new Error(res.data.error || "Error al restaurar perfil");
      }
      mostrarToast(`Perfil "${nombre || 'Libre'}" restaurado con éxito 🎉`, 'success');
      cargarDatos();
    }).catch(err => {
      console.error("Error al restaurar perfil:", err);
      mostrarToast("Error al restaurar perfil", "danger");
      cargarDatos();
    });
  };

  const handleEliminarDefinitivo = (id, nombre) => {
    if (!id) {
      mostrarToast("ID de perfil no encontrado", "danger");
      return;
    }

    if (!window.confirm(`⚠️ ¿Estás seguro de ELIMINAR DEFINITIVAMENTE el perfil de "${nombre}"?\nEsta acción no se puede deshacer.`)) return;

    setSelectedTrashIds(prev => prev.filter(item => item !== id));
    setDataEliminados(prev => (Array.isArray(prev) ? prev.filter(item => item.id !== id) : []));

    axios.delete(`${api}/clientes/destruir/${id}`).then(res => {
      if (res.data && res.data.success === false) {
        throw new Error(res.data.error || "Error al eliminar definitivamente");
      }
      mostrarToast(`Perfil "${nombre}" eliminado permanentemente`, 'danger');
      cargarDatos();
    }).catch(err => {
      console.error("Error al eliminar definitivamente:", err);
      mostrarToast("Error al eliminar definitivamente", "danger");
      cargarDatos();
    });
  };

  const handleBorrarSeleccionadosPapelera = async () => {
    if (selectedTrashIds.length === 0 || isBulkDeleting) return;

    const cantidad = selectedTrashIds.length;
    const confirmacion = window.confirm(
      `¿Eliminar definitivamente ${cantidad} perfil${cantidad > 1 ? 'es' : ''}?\n\nEsta acción no se puede deshacer.`
    );
    if (!confirmacion) return;

    setIsBulkDeleting(true);
    const idsABorrar = [...selectedTrashIds];

    try {
      const resultados = await Promise.allSettled(
        idsABorrar.map(id => axios.delete(`${api}/clientes/destruir/${id}`))
      );

      const idsExitosos = [];
      const idsFallidos = [];

      resultados.forEach((res, index) => {
        const id = idsABorrar[index];
        if (res.status === 'fulfilled' && res.value?.data?.success !== false) {
          idsExitosos.push(id);
        } else {
          idsFallidos.push(id);
        }
      });

      if (idsExitosos.length > 0) {
        setDataEliminados(prev => (Array.isArray(prev) ? prev.filter(item => !idsExitosos.includes(item.id)) : []));
        setSelectedTrashIds(prev => prev.filter(id => !idsExitosos.includes(id)));
      }

      if (idsFallidos.length === 0) {
        mostrarToast(`✅ ${idsExitosos.length} perfil${idsExitosos.length > 1 ? 'es' : ''} eliminado${idsExitosos.length > 1 ? 's' : ''} definitivamente`, 'danger');
      } else if (idsExitosos.length > 0) {
        mostrarToast(`⚠️ ${idsExitosos.length} eliminado${idsExitosos.length > 1 ? 's' : ''} correctamente. ${idsFallidos.length} no ${idsFallidos.length > 1 ? 'pudieron' : 'pudo'} eliminarse.`, 'warning');
      } else {
        mostrarToast('❌ No se pudieron eliminar los perfiles seleccionados', 'danger');
      }

      cargarDatos();
    } catch (err) {
      console.error("Error en borrado masivo:", err);
      mostrarToast("Error durante el borrado masivo", "danger");
      cargarDatos();
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleVaciarPapelera = async () => {
    if (safeEliminados.length === 0 || isBulkDeleting) return;

    const cantidad = safeEliminados.length;
    const confirmacion = window.confirm(
      `¿Vaciar completamente la Papelera?\n\nSe eliminarán definitivamente ${cantidad} perfil${cantidad > 1 ? 'es' : ''}.\n\nEsta acción NO se puede deshacer.`
    );
    if (!confirmacion) return;

    setIsBulkDeleting(true);
    const todosLosIds = safeEliminados.map(p => p.id).filter(Boolean);

    try {
      const resultados = await Promise.allSettled(
        todosLosIds.map(id => axios.delete(`${api}/clientes/destruir/${id}`))
      );

      const idsExitosos = [];
      const idsFallidos = [];

      resultados.forEach((res, index) => {
        const id = todosLosIds[index];
        if (res.status === 'fulfilled' && res.value?.data?.success !== false) {
          idsExitosos.push(id);
        } else {
          idsFallidos.push(id);
        }
      });

      if (idsExitosos.length > 0) {
        setDataEliminados(prev => (Array.isArray(prev) ? prev.filter(item => !idsExitosos.includes(item.id)) : []));
        setSelectedTrashIds(prev => prev.filter(id => !idsExitosos.includes(id)));
      }

      if (idsFallidos.length === 0) {
        mostrarToast(`🗑️ Papelera vaciada: ${idsExitosos.length} perfil${idsExitosos.length > 1 ? 'es' : ''} eliminado${idsExitosos.length > 1 ? 's' : ''} definitivamente`, 'danger');
      } else if (idsExitosos.length > 0) {
        mostrarToast(`⚠️ ${idsExitosos.length} eliminados. ${idsFallidos.length} no pudieron eliminarse.`, 'warning');
      } else {
        mostrarToast('❌ No se pudo vaciar la papelera', 'danger');
      }

      cargarDatos();
    } catch (err) {
      console.error("Error al vaciar la papelera:", err);
      mostrarToast("Error al vaciar la papelera", "danger");
      cargarDatos();
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleBorrarSeleccionadosVencimientos = async () => {
    if (selectedExpirationIds.length === 0 || isBulkDeletingExpiration) return;

    const cantidad = selectedExpirationIds.length;
    const confirmacion = window.confirm(
      `¿Eliminar ${cantidad} perfil${cantidad > 1 ? 'es seleccionados' : ' seleccionado'}?\n\nLos perfiles serán enviados a la Papelera y podrán restaurarse posteriormente.\n\nEsta acción NO los eliminará definitivamente.`
    );
    if (!confirmacion) return;

    setIsBulkDeletingExpiration(true);
    const idsABorrar = [...selectedExpirationIds];

    try {
      const resultados = await Promise.allSettled(
        idsABorrar.map(id => axios.delete(`${api}/delete/${id}`))
      );

      const idsExitosos = [];
      const idsFallidos = [];

      resultados.forEach((res, index) => {
        const id = idsABorrar[index];
        if (res.status === 'fulfilled' && res.value?.data?.success !== false) {
          idsExitosos.push(id);
        } else {
          idsFallidos.push(id);
        }
      });

      if (idsExitosos.length > 0) {
        const exitososSet = new Set(idsExitosos);
        const perfilesParaPapelera = safeClientes
          .filter(c => exitososSet.has(c.id))
          .map(c => ({ ...c, eliminado: 1, fecha_eliminacion: new Date().toISOString() }));

        setDataClientes(prev => (Array.isArray(prev) ? prev.filter(c => !exitososSet.has(c.id)) : []));
        if (perfilesParaPapelera.length > 0) {
          setDataEliminados(prev => (Array.isArray(prev) ? [...perfilesParaPapelera, ...prev] : perfilesParaPapelera));
        }
        setSelectedExpirationIds(prev => prev.filter(id => !exitososSet.has(id)));
      }

      if (idsFallidos.length === 0) {
        mostrarToast(`✅ ${idsExitosos.length} perfil${idsExitosos.length > 1 ? 'es' : ''} enviado${idsExitosos.length > 1 ? 's' : ''} a la Papelera 🗑️`, 'warning');
      } else if (idsExitosos.length > 0) {
        mostrarToast(`⚠️ ${idsExitosos.length} enviado${idsExitosos.length > 1 ? 's' : ''} a la Papelera. ${idsFallidos.length} no ${idsFallidos.length > 1 ? 'pudieron' : 'pudo'} eliminarse.`, 'warning');
      } else {
        mostrarToast('❌ No se pudieron enviar los perfiles a la Papelera', 'danger');
      }

      cargarDatos();
    } catch (err) {
      console.error("Error en borrado masivo de vencimientos:", err);
      mostrarToast("Error durante el borrado de vencimientos", "danger");
      cargarDatos();
    } finally {
      setIsBulkDeletingExpiration(false);
    }
  };

  // CLIENTES HANDLERS
  const handleFechaInicioChange = (e) => { 
      const f = e.target.value; 
      setFormCliente({ ...formCliente, fecha_inicio: f, fecha_fin: calcularSiguienteVencimiento(f, 30) }); 
  };

  const handleDiasExtrasChange = (e) => {
    const val = e.target.value;
    if (val === '') {
      setDiasExtras('');
      return;
    }
    const clean = val.replace(/[^\d]/g, '');
    if (clean === '') {
      setDiasExtras('');
      return;
    }
    const num = parseInt(clean, 10);
    if (!isNaN(num) && num >= 0) {
      setDiasExtras(num);
    }
  };
  
  const limpiarFormCliente = () => { 
      setFormCliente({ 
        nombre: '', celular: '', servicio: 'Netflix', perfil: '', pin: '', 
        correo: '', contrasena: '', 
        fecha_inicio: dayjs().format('YYYY-MM-DD'), 
        fecha_fin: dayjs().add(30, 'day').format('YYYY-MM-DD'), 
        monto: '', 
        account_id: null 
      }); 
      setDiasExtras(0);
      setComentarioAjuste('');
      setEditandoCliente(false); 
      setClienteEditarId(null); 
  };

  const handleGenerarPerfilLibreCuenta = (servicio, correo, contrasena, clientesExistentes = [], accountId = null) => {
    const perfilesPosibles = ['Perfil 1', 'Perfil 2', 'Perfil 3', 'Perfil 4', 'Perfil 5'];
    const ocupados = clientesExistentes.map(c => (c.perfil || '').trim().toLowerCase());
    let perfilLibre = perfilesPosibles.find(p => !ocupados.includes(p.toLowerCase()));
    if (!perfilLibre) {
      perfilLibre = `Perfil ${clientesExistentes.length + 1}`;
    }

    setFormCliente({
      nombre: '>>> LIBRE <<<',
      celular: '',
      servicio: servicio || 'Netflix',
      perfil: perfilLibre,
      pin: '',
      correo: correo || '',
      contrasena: contrasena || '',
      fecha_inicio: dayjs().format('YYYY-MM-DD'),
      fecha_fin: dayjs().add(30, 'day').format('YYYY-MM-DD'),
      monto: '0',
      account_id: accountId || null
    });
    setDiasExtras(0);
    setComentarioAjuste('');
    setEditandoCliente(false);
    setClienteEditarId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prepararPayloadCliente = (form) => {
      const nombreFinal = form.nombre && form.nombre.trim() ? form.nombre.trim() : '>>> LIBRE <<<';
      return {
          nombre: nombreFinal,
          nombre_cliente: nombreFinal,
          full_name: nombreFinal,
          celular: form.celular || '',
          numero_celular: form.celular || '',
          phone: form.celular || '',
          pin: form.pin || '', 
          pin_perfil: form.pin || '',
          fecha_fin: form.fecha_fin,
          fecha_finalizacion: form.fecha_fin,
          end_date: form.fecha_fin,
          servicio: form.servicio,
          service: form.servicio,
          perfil: form.perfil || 'Perfil 1',
          profile_label: form.perfil || 'Perfil 1',
          correo: form.correo || '',
          email: form.correo || '',
          contrasena: form.contrasena || '',
          password: form.contrasena || '',
          fecha_inicio: form.fecha_inicio,
          start_date: form.fecha_inicio,
          monto: form.monto === '' ? 0 : form.monto,
          amount: form.monto === '' ? 0 : form.monto,
          account_id: form.account_id || undefined,
          cuenta_id: form.account_id || undefined,
          accountId: form.account_id || undefined
      };
  };
  
  const guardarCliente = async (e) => { 
      if (e) e.preventDefault(); 
      if (cargandoGuardar) return;
      setCargandoGuardar(true);
      const payload = prepararPayloadCliente(formCliente);
      try {
          const res = await axios.post(`${api}/clientes`, payload);
          if (res.data && res.data.success === false) {
              mostrarToast(`❌ Error: ${res.data.error || 'No se pudo registrar'}`, 'danger');
              return;
          }
          mostrarToast('✅ Cliente / Perfil registrado con éxito 🎉', 'success');
          cargarDatos();
          limpiarFormCliente();
      } catch (err) {
          console.error('Error al registrar cliente:', err.response?.data || err);
          const msg = err.response?.data?.error || err.response?.data?.message || 'Error de conexión al registrar.';
          mostrarToast(`❌ Error: ${msg}`, 'danger');
      } finally {
          setCargandoGuardar(false);
      }
  };
  
  const actualizarCliente = async (e) => { 
      if (e) e.preventDefault(); 
      if (cargandoGuardar) return;

      const diasExtrasNum = parseInt(diasExtras, 10) || 0;
      if (diasExtrasNum > 0 && !comentarioAjuste.trim()) {
          mostrarToast('Ingresa el motivo por el que estás agregando días extras.', 'warning');
          return;
      }

      setCargandoGuardar(true);

      const fechaFinFinal = diasExtrasNum > 0 
          ? sumarDiasFechaCalendario(formCliente.fecha_fin, diasExtrasNum) 
          : formCliente.fecha_fin;

      const payload = {
          ...prepararPayloadCliente({
              ...formCliente,
              fecha_fin: fechaFinFinal
          }),
          dias_extras: diasExtrasNum,
          motivo_ajuste: comentarioAjuste.trim() || undefined,
          comentario: comentarioAjuste.trim() || undefined
      };

      try {
          const res = await axios.put(`${api}/update/${clienteEditarId}`, payload);
          if (res.data && res.data.success === false) {
              mostrarToast(`❌ Error: ${res.data.error || 'No se pudo actualizar'}`, 'danger');
              return;
          }
          mostrarToast('✅ Perfil / Cliente asignado y actualizado 🎉', 'success');
          cargarDatos();
          limpiarFormCliente();
      } catch (err) {
          console.error('Error al actualizar cliente/perfil:', err.response?.data || err);
          const msg = err.response?.data?.error || err.response?.data?.message || 'No se pudo asignar el cliente al perfil.';
          mostrarToast(`❌ Error: ${msg}`, 'danger');
      } finally {
          setCargandoGuardar(false);
      }
  };

  const handleEditarCliente = (c) => { 
      setFormCliente({
          nombre: c.nombre_cliente || c.nombre || '',
          celular: c.numero_celular || c.celular || '',
          servicio: c.servicio || 'Netflix',
          perfil: c.perfil || '',
          pin: pinsRevelados[c.id] || c.pin_perfil || c.pin || '',
          correo: c.correo || c.email || '',
          contrasena: c.contrasena || '',
          fecha_inicio: c.fecha_inicio ? c.fecha_inicio.split('T')[0] : dayjs().format('YYYY-MM-DD'),
          fecha_fin: c.fecha_finalizacion ? c.fecha_finalizacion.split('T')[0] : (c.fecha_fin ? c.fecha_fin.split('T')[0] : dayjs().add(30, 'day').format('YYYY-MM-DD')),
          monto: c.monto !== undefined && c.monto !== null ? c.monto : '',
          account_id: c.account_id || c.cuenta_id || null
      });
      setDiasExtras(0);
      setComentarioAjuste('');
      setEditandoCliente(true);
      setClienteEditarId(c.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // --- HANDLERS MODAL RENOVACIÓN DE CLIENTE ---
  const handleAbrirModalRenovar = (cliente) => {
    if (!cliente) return;
    
    const fechaVenceActual = getFechaVencimientoStr(cliente);
    const hoy = dayjs().format('YYYY-MM-DD');
    const fechaInicioCalculada = (!fechaVenceActual || dayjs(fechaVenceActual).isBefore(dayjs(), 'day')) 
      ? hoy 
      : (fechaVenceActual.includes('T') ? fechaVenceActual.split('T')[0] : fechaVenceActual);
    
    const nuevaFechaFinal = sumarDiasFechaLocal(fechaInicioCalculada, 30);

    setFormRenovar({
      id: cliente.id,
      nombre_cliente: cliente.nombre_cliente || cliente.nombre || 'Cliente',
      servicio: cliente.servicio || 'Netflix',
      perfil: cliente.perfil || 'Perfil 1',
      pin: pinsRevelados[cliente.id] || cliente.pin_perfil || cliente.pin || '',
      monto: cliente.monto !== undefined && cliente.monto !== null ? cliente.monto : 0,
      fecha_inicio: fechaInicioCalculada,
      fecha_finalizacion: nuevaFechaFinal
    });
    setPerfilRenovar(cliente);
  };

  const handleFechaInicioRenovarChange = (e) => {
    const nuevaFechaInicio = e.target.value;
    const nuevaFechaFinal = sumarDiasFechaLocal(nuevaFechaInicio, 30);
    setFormRenovar(prev => ({
      ...prev,
      fecha_inicio: nuevaFechaInicio,
      fecha_finalizacion: nuevaFechaFinal
    }));
  };

  const ejecutarRenovacion = () => {
    if (!formRenovar.id) return;
    setCargandoRenovacion(true);

    const payload = {
      id: formRenovar.id,
      subscription_id: formRenovar.id,
      monto: formRenovar.monto === '' ? 0 : Number(formRenovar.monto),
      dias: 30,
      cliente_nombre: formRenovar.nombre_cliente,
      fecha_inicio: formRenovar.fecha_inicio,
      fecha_finalizacion: formRenovar.fecha_finalizacion,
      nuevaFecha: formRenovar.fecha_finalizacion,
      vence: formRenovar.fecha_finalizacion,
      plataforma: formRenovar.servicio,
      servicio: formRenovar.servicio,
      perfil: formRenovar.perfil
    };

    axios.post(`${api}/clientes/renovar/${formRenovar.id}`, payload)
      .then(res => {
        if (res.data && res.data.success === false) {
          throw new Error(res.data.error || "No se pudo renovar");
        }
        const [fy, fm, fd] = formRenovar.fecha_finalizacion.split('-');
        setSelectedExpirationIds(prev => prev.filter(item => item !== formRenovar.id));
        mostrarToast(`🎉 Perfil de "${formRenovar.nombre_cliente}" renovado hasta ${fd}/${fm}/${fy}!`, 'success');
        setPerfilRenovar(null);
        cargarDatos();
      })
      .catch(err => {
        console.error("Error al renovar perfil:", err);
        mostrarToast(`Error al renovar: ${err.response?.data?.message || err.message}`, 'danger');
      })
      .finally(() => {
        setCargandoRenovacion(false);
      });
  };

  // --- HANDLERS ENVIAR CUENTA ---
  const esPerfilLibre = (c) => {
    if (!c) return true;
    const n = (c.nombre_cliente || c.nombre || '').trim();
    if (!n) return true;
    if (n.toUpperCase().includes('LIBRE')) return true;
    return false;
  };

  const handleEnviarCuenta = async (c, correo, servicio, stockEncontrado) => {
    if (!c) return;
    try {
      const rawService = servicio || c.servicio || c.service || c?.streaming_accounts?.service || 'Streaming';
      const platformName = String(rawService).trim().toUpperCase();
      const email = String(correo || c.correo || c.email || stockEncontrado?.correo || '').trim();

      // Resolver contraseña
      let password = '';
      if (stockEncontrado?.id && credencialesReveladas[stockEncontrado.id]) {
        password = credencialesReveladas[stockEncontrado.id];
      } else if (stockEncontrado?.id) {
        try {
          const { data } = await axios.get(`${api}/inventario/${stockEncontrado.id}/credencial`, { headers: NO_CACHE_HEADERS });
          if (data && data.secret) {
            password = data.secret;
          }
        } catch (_) {
          password = (stockEncontrado.contrasena && stockEncontrado.contrasena !== '••••••••') ? stockEncontrado.contrasena : (c.contrasena && c.contrasena !== '••••••••' ? c.contrasena : '');
        }
      } else if (c.contrasena && c.contrasena !== '••••••••') {
        password = c.contrasena;
      }
      if (password === 'undefined' || password === 'null' || !password) {
        password = (stockEncontrado?.contrasena && stockEncontrado.contrasena !== '••••••••') ? stockEncontrado.contrasena : (c.contrasena && c.contrasena !== '••••••••' ? c.contrasena : '');
        if (password === 'undefined' || password === 'null') password = '';
      }

      // Resolver PIN
      let pin = '';
      if (pinsRevelados[c.id]) {
        pin = pinsRevelados[c.id];
      } else if (c.id && (c.pin_perfil === '••••••••' || c.pin === '••••••••' || !c.pin)) {
        try {
          const { data } = await axios.get(`${api}/clientes/${c.id}/pin`, { headers: NO_CACHE_HEADERS });
          if (data && data.pin) {
            pin = data.pin;
          }
        } catch (_) {
          pin = (c.pin_perfil && c.pin_perfil !== '••••••••') ? c.pin_perfil : (c.pin && c.pin !== '••••••••' ? c.pin : '');
        }
      } else {
        pin = (c.pin_perfil && c.pin_perfil !== '••••••••') ? c.pin_perfil : (c.pin && c.pin !== '••••••••' ? c.pin : '');
      }

      const pinLimpio = String(pin || '').trim();
      const pinFinal = (!pinLimpio || pinLimpio === 'null' || pinLimpio === 'undefined' || pinLimpio === '••••••••') ? 'Sin PIN' : pinLimpio;

      const perfil = String(c.perfil || 'Perfil 1').trim();

      const fechaFinStr = getFechaVencimientoStr(c);
      let fechaVencimiento = '-';
      if (fechaFinStr) {
        const fechaLimpia = String(fechaFinStr).split('T')[0];
        const partes = fechaLimpia.split('-');
        if (partes.length === 3 && partes[0] && partes[1] && partes[2]) {
          fechaVencimiento = `${partes[2]}/${partes[1]}/${partes[0]}`;
        } else if (dayjs(fechaFinStr).isValid()) {
          fechaVencimiento = dayjs(fechaFinStr).format('DD/MM/YYYY');
        } else {
          fechaVencimiento = fechaFinStr;
        }
      }

      const plantilla = `🎉 *¡GRACIAS POR TU COMPRA!* 🎉
Tu servicio ha sido activado con éxito. A continuación, te compartimos los accesos de tu cuenta:

━━━━━━━━━━━━━━━━━━━━
🔥 *${String(platformName || servicio || 'STREAMING').toUpperCase()}*

✅ Correo: ${email}
✅ Contraseña: ${password}
✅ Perfil: ${perfil}
✅ PIN: ${pinFinal}

📌 Dispositivos: 1 Pantalla
📅 Vencimiento: ${fechaVencimiento}
━━━━━━━━━━━━━━━━━━━━

📢 *COMUNIDAD & PROMOCIONES*
Únete a nuestro grupo oficial para recibir novedades, promociones exclusivas y ofertas especiales:
👉 https://chat.whatsapp.com/IGe696HcONZDrLKsNKL4QL

⏰ *HORARIOS DE ATENCIÓN*
• *Ventas:* 
  - Lunes a Domingo: 9:00 AM – 12:00 PM
• *Soporte Técnico:*
  - Lunes a Sábado: 9:00 AM – 8:00 PM
  - Domingos y Feriados: 10:00 AM – 8:00 PM

⚠️ *TÉRMINOS Y CONDICIONES*
• *Reembolsos:* Una vez entregada la cuenta, no se realizan devoluciones.
• *Garantía:* Cobertura total durante todo el mes contratado.
• *Uso:* Respetar el perfil asignado y el límite de 1 dispositivo para evitar bloqueos.`;

      setModalEnviarCuenta({ plantilla });
    } catch (_) {
      mostrarToast('Error al generar plantilla para enviar', 'danger');
    }
  };

  const handleCopiarPlantilla = async () => {
    if (!modalEnviarCuenta?.plantilla) return;
    try {
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(modalEnviarCuenta.plantilla);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = modalEnviarCuenta.plantilla;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      mostrarToast('¡Plantilla copiada al portapapeles! 📋', 'success');
    } catch (_) {
      mostrarToast('No se pudo copiar la plantilla', 'danger');
    }
  };

  // --- ACTUALIZACIÓN DE CLAVE ---
  const normalizeAppName = (servicio) => {
    const s = (servicio || '').toLowerCase().trim();
    if (s.includes('netflix')) return 'NETFLIX';
    if (s.includes('disney')) return 'DISNEY+';
    if (s.includes('hbo') || s.includes('max')) return 'HBO MAX';
    if (s.includes('prime') || s.includes('amazon')) return 'PRIME VIDEO';
    if (s.includes('crunchyroll')) return 'CRUNCHYROLL';
    if (s.includes('paramount')) return 'PARAMOUNT+';
    if (s.includes('gpt')) return 'GPT';
    if (s.includes('youtube')) return 'YOUTUBE';
    if (s.includes('spotify')) return 'SPOTIFY';
    return servicio.trim().toUpperCase();
  };

  const generatePasswordUpdateMessage = (servicio, correo, password) => {
    const appName = normalizeAppName(servicio);
    return `*=======================*\n*ACTUALIZACION CLAVE ${appName}*\nEmail: ${correo}\nContraseña: ${password}\n*=======================*`;
  };

  const handleActualizacionClave = (servicio, correo, passwordCuenta) => {
    const mensaje = generatePasswordUpdateMessage(servicio, correo, passwordCuenta || '');
    setModalActualizacionClave({ mensaje, servicio });
  };

  const handleCopiarActualizacionClave = async () => {
    if (!modalActualizacionClave?.mensaje) return;
    try {
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(modalActualizacionClave.mensaje);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = modalActualizacionClave.mensaje;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      mostrarToast('¡Mensaje copiado al portapapeles! 📋', 'success');
    } catch (_) {
      mostrarToast('No se pudo copiar el mensaje', 'danger');
    }
  };

  // --- RECORDATORIO DE RENOVACIÓN ---
  const generateRenewalReminderMessage = ({ clientName, serviceName, expirationDate }) => {
    return `*================================*\n*🔔 RECORDATORIO DE RENOVACIÓN*\nHola *${clientName}* 👋\n\nTu perfil de *${serviceName}* está próximo a vencer.\n\n📅 Fecha de vencimiento: *${expirationDate}*\n\nSi deseas continuar con el servicio, puedes realizar tu renovación.\n\n✅ Si ya realizaste el pago, omite este mensaje.\n\n_*LeoTech Streaming*_ 🕷️🕸️\n*================================*`;
  };

  const handleRecordatorioRenovacion = (c, servicio) => {
    if (!c) return;

    const clientName = (c.nombre_cliente || c.nombre || '').trim() || '>>> LIBRE <<<';
    const serviceName = normalizeAppName(servicio || c.servicio || '');

    const fechaFinStr = getFechaVencimientoStr(c);
    let expirationDate = '-';
    if (fechaFinStr) {
      const fechaLimpia = String(fechaFinStr).split('T')[0];
      const partes = fechaLimpia.split('-');
      if (partes.length === 3 && partes[0] && partes[1] && partes[2]) {
        expirationDate = `${partes[2]}/${partes[1]}`;
      } else if (dayjs(fechaFinStr).isValid()) {
        expirationDate = dayjs(fechaFinStr).add(10, 'hour').format('DD/MM');
      } else {
        expirationDate = fechaFinStr;
      }
    }

    const mensaje = generateRenewalReminderMessage({ clientName, serviceName, expirationDate });
    setModalRecordatorioRenovacion({ mensaje, clientName });
  };

  const handleCopiarRecordatorio = async () => {
    if (!modalRecordatorioRenovacion?.mensaje) return;
    try {
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(modalRecordatorioRenovacion.mensaje);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = modalRecordatorioRenovacion.mensaje;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setRecordatorioCopiadoId(true);
      setTimeout(() => setRecordatorioCopiadoId(null), 2500);
    } catch (_) {
      mostrarToast('No se pudo copiar el mensaje', 'danger');
    }
  };

  // STOCK HANDLERS
  const handleFechaStockChange = (e) => { const ne = e.target.value; if (editandoStock) credentialAttemptKeyRef.current = ''; setFormStock({ ...formStock, fecha_entrada: ne, fecha_vencimiento: sumarDiasFechaLocal(ne, 30) }); };
  const updateStockForm = changes => { if (editandoStock) credentialAttemptKeyRef.current = ''; setFormStock(current => ({ ...current, ...changes })); };
  const limpiarFormStock = () => { const hoyStr = dayjs().format('YYYY-MM-DD'); setFormStock({ correo: '', contrasena: '', servicio: 'Netflix', costo: '', fecha_entrada: hoyStr, fecha_vencimiento: sumarDiasFechaLocal(hoyStr, 30), mode: 'new', sourceAccountId: '', reason: '', note: '' }); replacementAttemptKeyRef.current = ''; credentialAttemptKeyRef.current = ''; setReplacementPreview(null); setEditandoStock(false); setStockEditarId(null); };
  const buildStockPayload = (form) => {
    const rawCorreo = String(form.correo || form.email || '').replace(/\s+/g, '').trim();
    const rawServicio = String(form.servicio || '').trim();
    const servicioNormalizado = rawServicio.toLowerCase() === 'gpt' ? 'GPT' : rawServicio;
    const costoNormalizado = Number(form.costo || form.monto || 0);

    const entradaRaw = form.entrada || form.fecha_entrada || form.fecha_inicio;
    const venceRaw = form.vence || form.fecha_vencimiento || form.fecha_vence;

    const entradaFormateada = dayjs(entradaRaw).isValid()
      ? dayjs(entradaRaw).format('YYYY-MM-DD')
      : String(entradaRaw || '').trim().split('T')[0];

    const venceFormateado = dayjs(venceRaw).isValid()
      ? dayjs(venceRaw).format('YYYY-MM-DD')
      : String(venceRaw || '').trim().split('T')[0];

    return {
      correo: rawCorreo,
      email: rawCorreo,
      servicio: servicioNormalizado,
      contrasena: form.contrasena || '',
      costo: costoNormalizado,
      monto: costoNormalizado,
      entrada: entradaFormateada,
      fecha_entrada: entradaFormateada,
      vence: venceFormateado,
      fecha_vencimiento: venceFormateado,
      capacidad: Number(form.capacidad) || 5,
      mode: form.mode || 'new', sourceAccountId: form.sourceAccountId || '',
      reason: form.reason || '', note: form.note || ''
    };
  };

  const guardarCuenta = (e) => {
    e.preventDefault();
    const payload = buildStockPayload(formStock);
    if (payload.mode === 'replacement') {
      if (!payload.sourceAccountId || !payload.reason.trim()) {
        mostrarToast('Selecciona la cuenta que falla e indica el motivo del reemplazo', 'danger');
        return;
      }
      const movedCount = replacementPreview?.profilesToMove ?? '?';
      if (!window.confirm(`Se trasladarán ${movedCount} perfiles vigentes a la nueva cuenta. La cuenta que falla quedará apartada del stock. ¿Continuar?`)) return;
      const signature = JSON.stringify(payload);
      if (!replacementAttemptKeyRef.current || replacementAttemptKeyRef.current.signature !== signature) {
        replacementAttemptKeyRef.current = { signature, key: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}` };
      }
      payload.idempotencyKey = replacementAttemptKeyRef.current.key;
    }

    const authToken = localStorage.getItem('token') || sessionStorage.getItem('authToken') || '';

    axios.post(`${api}/inventario`, payload, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token') || authToken}`,
        ...(payload.mode === 'replacement' ? { 'Idempotency-Key': payload.idempotencyKey } : {})
      }
    })
      .then(({ data }) => {
        const isReplacement = payload.mode === 'replacement';
        if (isReplacement) {
          const source = safeInventario.find(account => String(account.id) === String(payload.sourceAccountId));
          alert(`✅ Reemplazo registrado\n${source?.correo || 'Cuenta origen'} → ${payload.correo}\nMotivo: ${payload.reason}\nPerfiles trasladados: ${data?.movedProfiles ?? 0}\nEspacios libres: ${data?.remainingCapacity ?? '—'}\nTareas de entrega creadas: ${data?.tasksCreated ?? 0}`);
        } else {
          alert("✅ Cuenta Agregada");
        }
        mostrarToast(isReplacement ? "✅ Reemplazo registrado" : "✅ Cuenta Agregada", "success");
        if (isReplacement) setCredentialTasksVersion(version => version + 1);
        cargarDatos();
        limpiarFormStock();
      })
      .catch((error) => {
        const mensajeError = error.response?.data?.error || error.message;
        alert(`Error: ${mensajeError}`);
        mostrarToast(mensajeError, "danger");
      });
  };

  const actualizarCuenta = (e) => {
    e.preventDefault();
    const payload = buildStockPayload(formStock);

    const authToken = localStorage.getItem('token') || sessionStorage.getItem('authToken') || '';
    const hasNewCredential = Boolean(payload.contrasena && payload.contrasena !== '••••••••');
    if (hasNewCredential && !credentialAttemptKeyRef.current) {
      credentialAttemptKeyRef.current = window.crypto?.randomUUID?.() || `account-credential-${Date.now()}-${Math.random()}`;
    }

    axios.put(`${api}/inventario/${stockEditarId}`, payload, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token') || authToken}`,
        ...(hasNewCredential ? { 'Idempotency-Key': credentialAttemptKeyRef.current } : {})
      }
    })
      .then(({ data }) => {
        const changed = Boolean(data?.credentialChanged);
        alert(changed ? `✅ Clave actualizada. Tareas creadas: ${data?.tasksCreated || 0}` : "✅ Stock Actualizado");
        mostrarToast(changed ? `Clave actualizada · ${data?.tasksCreated || 0} tareas para entregar` : "✅ Stock Actualizado", "success");
        credencialesCargadasRef.current.delete(stockEditarId);
        setCredencialesReveladas(prev => { const next = { ...prev }; delete next[stockEditarId]; return next; });
        credentialAttemptKeyRef.current = '';
        if (changed) setCredentialTasksVersion(version => version + 1);
        cargarDatos();
        limpiarFormStock();
      })
      .catch((error) => {
        const mensajeError = error.response?.data?.error || error.message;
        alert(`Error: ${mensajeError}`);
        mostrarToast(mensajeError, "danger");
      });
  };
  const handleEditarStock = (i) => { credentialAttemptKeyRef.current = ''; setFormStock({ correo: i.correo, contrasena: '', servicio: i.servicio, costo: i.costo, fecha_entrada: i.fecha_entrada ? i.fecha_entrada.split('T')[0] : '', fecha_vencimiento: i.fecha_vencimiento ? i.fecha_vencimiento.split('T')[0] : '' }); setEditandoStock(true); setStockEditarId(i.id); };

  const cambiarEstadoCaidaStock = async (cuenta, action) => {
    const motivo = window.prompt(action === 'report' ? '¿Cuál es el motivo de la caída?' : '¿Por qué se restaura esta cuenta?');
    if (!motivo || !motivo.trim()) return;
    const nota = window.prompt('Nota adicional (opcional):') || '';
    const authToken = localStorage.getItem('token') || sessionStorage.getItem('authToken') || '';
    try {
      await axios.post(`${api}/inventario/${cuenta.id}/${action === 'report' ? 'reportar-caida' : 'restaurar'}`, {
        motivo: motivo.trim(), nota: nota.trim()
      }, { headers: { Authorization: `Bearer ${authToken}` } });
      mostrarToast(action === 'report' ? 'Cuenta marcada como caída y retirada del stock disponible' : 'Cuenta restaurada', 'success');
      cargarDatos();
    } catch (error) {
      mostrarToast(error.response?.data?.error || error.response?.data?.message || 'No se pudo actualizar el estado de la cuenta', 'danger');
    }
  };

  const registrarCambioClaveExterno = async cuenta => {
    const contrasena = window.prompt(`Ingresa la nueva clave de ${cuenta.correo}. Se guardará cifrada.`);
    if (!contrasena || !contrasena.trim()) return;
    const nota = window.prompt('Motivo o proveedor (opcional). No escribas la clave aquí.') || '';
    const authToken = localStorage.getItem('token') || sessionStorage.getItem('authToken') || '';
    const signature = JSON.stringify([cuenta.id, contrasena.trim(), nota.trim()]);
    if (externalCredentialAttemptRef.current?.signature !== signature) {
      externalCredentialAttemptRef.current = {
        signature,
        key: window.crypto?.randomUUID?.() || `external-credential-${Date.now()}-${Math.random()}`
      };
    }
    const idempotencyKey = externalCredentialAttemptRef.current.key;
    try {
      const { data } = await axios.post(`${api}/inventario/${cuenta.id}/cambio-clave-externo`,
        { contrasena: contrasena.trim(), nota: nota.trim() },
        { headers: { Authorization: `Bearer ${authToken}`, 'Idempotency-Key': idempotencyKey } });
      if (data?.changed) {
        mostrarToast(`Clave registrada · ${data.tasksCreated || 0} tareas para entregar`, 'success');
      } else {
        mostrarToast('La clave ya coincide; se actualizará la bandeja de entregas', 'info');
      }
      externalCredentialAttemptRef.current = null;
      setCredentialTasksVersion(version => version + 1);
      setCredencialesReveladas(current => { const revealed = { ...current }; delete revealed[cuenta.id]; return revealed; });
    } catch (error) {
      mostrarToast(error.response?.data?.error || 'No se pudo registrar el cambio de clave', 'danger');
    }
  };

  const mostrarHistorialCaidaStock = async (cuenta) => {
    const authToken = localStorage.getItem('token') || sessionStorage.getItem('authToken') || '';
    try {
      const { data } = await axios.get(`${api}/inventario/${cuenta.id}/historial`, { headers: { Authorization: `Bearer ${authToken}` } });
      const texto = (data || []).map(evento => {
        const acciones = {
          reported_failed: 'Caída reportada', restored: 'Cuenta restaurada', replaced: 'Cuenta reemplazada',
          credential_changed: `Clave actualizada (${evento.reason || 'cambio'})`
        };
        const accion = acciones[evento.action] || 'Actividad de cuenta';
        const fecha = evento.createdAt ? dayjs(evento.createdAt).format('DD/MM/YYYY HH:mm') : '';
        return `${fecha} — ${accion}: ${evento.reason} · ${evento.actorName || 'Personal'}${evento.note ? ` (${evento.note})` : ''}`;
      }).join('\n');
      window.alert(texto || 'Esta cuenta todavía no tiene eventos de caída o restauración.');
    } catch (error) {
      mostrarToast(error.response?.data?.error || 'No se pudo cargar el historial', 'danger');
    }
  };

  // --- HANDLERS MODAL RENOVACIÓN DE STOCK ---
  const handleFechaInicioRenovarStockChange = (e) => {
    const nuevaFechaInicio = e.target.value;
    if (!nuevaFechaInicio) {
      setFormRenovarStock(prev => ({ ...prev, fechaInicio: '', nuevaFechaVence: '' }));
      return;
    }
    const nuevaFechaVenceCalculada = sumarDiasFechaLocal(nuevaFechaInicio, 30);
    setFormRenovarStock(prev => ({
      ...prev,
      fechaInicio: nuevaFechaInicio,
      nuevaFechaVence: nuevaFechaVenceCalculada
    }));
  };

  const handleAbrirModalRenovarStock = (cuenta) => {
    if (!cuenta || cuenta.id === undefined || cuenta.id === null) return;
    const targetId = cuenta.id;

    const fechaInicioStr = dayjs().format('YYYY-MM-DD');
    const venceCalculado = sumarDiasFechaLocal(fechaInicioStr, 30);

    const costoBase = (cuenta.costo !== undefined && cuenta.costo !== null && cuenta.costo !== '') 
      ? parseFloat(cuenta.costo) 
      : 0;

    setFormRenovarStock({
      id: targetId,
      correo: cuenta.correo || '',
      servicio: cuenta.servicio || 'Netflix',
      contrasena: cuenta.contrasena || '',
      monto: costoBase,
      fechaInicio: fechaInicioStr,
      nuevaFechaVence: venceCalculado,
      actualizarCostoBase: true
    });
    setStockRenovar(cuenta);
  };

  const handleConfirmarRenovacionStock = (e) => {
    e.preventDefault();
    if (!formRenovarStock || formRenovarStock.id === undefined || formRenovarStock.id === null) return;

    const targetId = formRenovarStock.id;
    const nuevoMontoNum = parseFloat(formRenovarStock.monto || 0);
    const fechaVencimientoInput = formRenovarStock.nuevaFechaVence;
    const switchActualizarCosto = Boolean(formRenovarStock.actualizarCostoBase);

    const payload = {
      id: targetId,
      monto: nuevoMontoNum,
      fechaInicio: formRenovarStock.fechaInicio,
      fecha_entrada: formRenovarStock.fechaInicio,
      nuevaFechaVence: fechaVencimientoInput,
      fecha_vencimiento: fechaVencimientoInput,
      actualizarCostoBase: switchActualizarCosto,
      servicio: formRenovarStock.servicio,
      correo: formRenovarStock.correo
    };

    setCargandoRenovacionStock(true);

    axios.post(`${api}/api/inventario/renovar`, payload)
      .then(res => {
        if (res.status === 200 && res.data && res.data.success !== false) {
          const cuentaResp = res.data.cuentaRenovada || res.data;
          const nuevaFechaFinal = cuentaResp.fecha_vencimiento || cuentaResp.nuevaFechaVence || payload.nuevaFechaVence;
          const nuevaFechaInicio = cuentaResp.fecha_entrada || cuentaResp.fechaInicio || payload.fechaInicio;

          mostrarToast(`🎉 Stock de ${formRenovarStock.servicio} (${formRenovarStock.correo}) renovado hasta ${dayjs(nuevaFechaFinal).format('DD/MM/YYYY')} por S/ ${nuevoMontoNum.toFixed(2)}!`, 'success');

          setDataInventario(prevInventario => 
            (Array.isArray(prevInventario) ? prevInventario.map(item => {
              if (String(item.id) === String(targetId)) {
                return {
                  ...item,
                  costo: switchActualizarCosto ? nuevoMontoNum : item.costo,
                  fecha_entrada: nuevaFechaInicio,
                  fecha_vencimiento: nuevaFechaFinal,
                  vence: nuevaFechaFinal
                };
              }
              return item;
            }) : [])
          );

          setStockRenovar(null);
          cargarDatos();
        } else {
          mostrarToast(`❌ Error: ${res.data?.message || res.data?.error || 'Error al renovar stock'}`, 'danger');
        }
      })
      .catch(err => {
        console.error(`Error al renovar stock:`, err);
        mostrarToast(`❌ Error al renovar stock: ${err.response?.data?.message || err.message}`, 'danger');
      })
      .finally(() => {
        setCargandoRenovacionStock(false);
      });
  };

  // ORDENAMIENTO DE SERVICIOS Y CUENTAS
  const serviciosOrdenados = useMemo(() => {
    return Object.keys(clientesAgrupados).sort((servA, servB) => {
      const getMinDateServ = (serv) => {
        let minTs = Infinity;
        Object.values(clientesAgrupados[serv]).forEach(perfiles => {
          perfiles.forEach(p => {
            const d = getFechaVencimientoStr(p);
            if (d) {
              const ts = dayjs(d).valueOf();
              if (ts < minTs) minTs = ts;
            }
          });
        });
        return minTs;
      };
      return getMinDateServ(servA) - getMinDateServ(servB);
    });
  }, [clientesAgrupados]);

  return (
    <div className="row position-relative">
        
        {/* 🔥 BOTÓN FLOTANTE NOTIFICACIONES Y PAPELERA */}
        <div className="position-absolute top-0 end-0 mt-n4 me-2 d-flex gap-2 align-items-center" style={{zIndex: 1000}}>
            <button 
                className="btn btn-light shadow position-relative rounded-pill border d-flex align-items-center gap-2 px-3" 
                onClick={() => {
                    setMostrarPapelera(true);
                    cargarDatos();
                }}
                style={{height: '42px'}}
                title="Historial de Perfiles Eliminados"
            >
                <i className="bi bi-trash3-fill text-danger" style={{fontSize: '1.1rem'}}></i>
                <span className="fw-bold text-dark d-none d-sm-inline" style={{fontSize: '0.85rem'}}>Papelera</span>
                {enPapelera.length > 0 && (
                    <span className="badge rounded-pill bg-danger">
                        {enPapelera.length}
                    </span>
                )}
            </button>
            <button 
                className="btn btn-light shadow position-relative rounded-circle border" 
                onClick={() => setMostrarNotificaciones(!mostrarNotificaciones)}
                style={{width: '42px', height: '42px'}}
                title="Vencimientos Próximos"
            >
                <i className={`bi ${mostrarNotificaciones ? 'bi-x-lg' : 'bi-bell-fill text-warning'}`} style={{fontSize: '1.1rem'}}></i>
                {clientesPorVencer.length > 0 && !mostrarNotificaciones && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                        {clientesPorVencer.length}
                        <span className="visually-hidden">vencimientos</span>
                    </span>
                )}
            </button>
        </div>

        {/* 📬 CENTRO DE NOTIFICACIONES */}
        {mostrarNotificaciones && (
            <div className="col-12 mb-4">
                <div className="card shadow border-0" style={{backgroundColor: '#fff8e1', borderLeft: '5px solid #ffc107'}}>
                    <div className="card-header bg-transparent border-0 d-flex flex-wrap justify-content-between align-items-center gap-2 p-3">
                        <div className="d-flex align-items-center gap-2">
                            <h5 className="mb-0 fw-bold text-dark">
                                <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                                Vencimientos Próximos ({clientesPorVencer.length})
                            </h5>
                            {selectedExpirationIds.length > 0 && (
                                <span className="badge bg-danger bg-opacity-10 text-danger border border-danger-subtle px-2 py-1 rounded-pill small">
                                    {selectedExpirationIds.length} seleccionado{selectedExpirationIds.length > 1 ? 's' : ''}
                                </span>
                            )}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            {clientesPorVencer.length > 0 && (
                                <>
                                    <div className="form-check d-flex align-items-center gap-1 me-2 mb-0">
                                        <input
                                            type="checkbox"
                                            id="check-todos-vencimientos"
                                            className="form-check-input mt-0"
                                            style={{ cursor: 'pointer' }}
                                            checked={todosVencimientosSeleccionados}
                                            ref={el => {
                                                if (el) el.indeterminate = algunosVencimientosSeleccionados;
                                            }}
                                            onChange={toggleSeleccionarTodosVencimientos}
                                            disabled={isBulkDeletingExpiration || idsVencimientos.length === 0}
                                        />
                                        <label 
                                            htmlFor="check-todos-vencimientos" 
                                            className="form-check-label small fw-bold text-dark user-select-none" 
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Seleccionar todos
                                        </label>
                                    </div>

                                    <button
                                        type="button"
                                        className="btn btn-danger btn-sm rounded-pill px-3 fw-bold d-inline-flex align-items-center gap-1 shadow-sm"
                                        onClick={handleBorrarSeleccionadosVencimientos}
                                        disabled={selectedExpirationIds.length === 0 || isBulkDeletingExpiration}
                                        title="Mover perfiles seleccionados a la Papelera"
                                    >
                                        {isBulkDeletingExpiration ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                <span>Eliminando...</span>
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-trash3-fill"></i>
                                                <span>Borrar seleccionados{selectedExpirationIds.length > 0 ? ` (${selectedExpirationIds.length})` : ''}</span>
                                            </>
                                        )}
                                    </button>
                                </>
                            )}
                            <button 
                                className="btn btn-sm btn-outline-secondary rounded-pill px-3" 
                                onClick={() => { setMostrarNotificaciones(false); setSelectedExpirationIds([]); }}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                    <div className="card-body p-0">
                        {clientesPorVencer.length === 0 ? (
                            <div className="p-4 text-center text-muted">🎉 ¡Todo al día! No hay vencimientos cercanos.</div>
                        ) : (
                            <div className="list-group list-group-flush">
                                {clientesPorVencer.map(c => {
                                    const fechaFinStr = getFechaVencimientoStr(c);
                                    const fechaFin = dayjs(fechaFinStr);
                                    const diasRestantes = fechaFin.diff(dayjs(), 'day');
                                    let estado = { color: 'bg-warning', texto: 'Pronto' };
                                    if (diasRestantes < 0) estado = { color: 'bg-danger', texto: 'VENCIDO' };
                                    if (diasRestantes === 0) estado = { color: 'bg-danger', texto: 'HOY' };
                                    
                                    const celular = c.numero_celular || c.celular || '';
                                    const isSelected = selectedExpirationIds.includes(c.id);
                                    
                                    const mensajeWsp = `Hola ${c.nombre_cliente || c.nombre}, te saluda LeoTech. Tu cuenta de ${c.servicio} vence el ${fechaFin.format('DD/MM')}. ¿Desearías renovar? 🚀`;
                                    const linkWsp = celular ? `https://wa.me/51${celular.replace(/\s/g, '')}?text=${encodeURIComponent(mensajeWsp)}` : '#';

                                    return (
                                        <div 
                                            key={c.id} 
                                            className={`list-group-item d-flex flex-wrap align-items-center justify-content-between py-3 gap-2 ${isSelected ? 'table-active' : ''}`} 
                                            style={{backgroundColor: isSelected ? '#ffeeba' : (diasRestantes < 0 ? '#fff5f5' : 'transparent')}}
                                        >
                                            <div className="d-flex align-items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input mt-0"
                                                    style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                                    checked={isSelected}
                                                    disabled={isBulkDeletingExpiration}
                                                    onChange={() => toggleSeleccionarPerfilVencimiento(c.id)}
                                                    title="Seleccionar perfil"
                                                />
                                                <div className={`badge ${estado.color} rounded-pill p-2`} style={{minWidth:'70px'}}>{estado.texto}</div>
                                                <div>
                                                    <div className="fw-bold fs-6">{c.nombre_cliente || c.nombre}</div>
                                                    <div className="small text-muted">{c.servicio} • Perfil: {c.perfil}</div>
                                                    <div className="small fw-bold text-danger">Vence: {fechaFin.format('DD/MM/YYYY')}</div>
                                                </div>
                                            </div>
                                            <div className="mt-2 mt-md-0 d-flex align-items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleAbrirModalRenovar(c)}
                                                    className="btn btn-success btn-sm rounded-pill fw-bold px-3 shadow-sm d-inline-flex align-items-center gap-1"
                                                    disabled={isBulkDeletingExpiration}
                                                    title="Renovar este perfil (+30 días)"
                                                >
                                                    <i className="bi bi-arrow-repeat me-1"></i>Renovar
                                                </button>
                                                {celular ? (
                                                    <a href={linkWsp} target="_blank" rel="noreferrer" className="btn btn-outline-success btn-sm rounded-pill fw-bold px-3 shadow-sm">
                                                        <i className="bi bi-whatsapp me-2"></i>Cobrar
                                                    </a>
                                                ) : (
                                                    <span className="badge bg-secondary">Sin número</span>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => handleEliminar(c, 'cliente')}
                                                    className="btn btn-sm btn-link p-0 text-decoration-none ms-1"
                                                    disabled={isBulkDeletingExpiration}
                                                    title="Mover a Papelera"
                                                    style={{ fontSize: '1rem', lineHeight: 1 }}
                                                >
                                                    ❌
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )}

        {/* DASHBOARD SUPERIOR */}
        <div className="col-12 mb-4 pt-3">
          <div className="row g-3">
              <div className="col-md-4"><div className="card text-white bg-success shadow-sm border-0" style={{borderRadius: '10px'}}><div className="card-body"><div className="d-flex justify-content-between align-items-center"><div><h6 className="card-title mb-0 opacity-75">Ventas Totales</h6><h3 className="fw-bold my-2">S/ {totalIngresosClientes.toFixed(2)}</h3><small className="opacity-75">{dataClientes.length} clientes activos</small></div><i className="bi bi-cash-coin fs-1 opacity-75"></i></div></div></div></div>
              <div className="col-md-4"><div className="card text-white bg-primary shadow-sm border-0" style={{borderRadius: '10px'}}><div className="card-body"><div className="d-flex justify-content-between align-items-center"><div><h6 className="card-title mb-0 opacity-75">Ganancia Real</h6><h3 className="fw-bold my-2">S/ {gananciaNetaClientes.toFixed(2)}</h3><small className="opacity-75">Inversión Stock: S/ {inversionRealStock.toFixed(2)}</small></div><i className="bi bi-graph-up-arrow fs-1 opacity-75"></i></div></div></div></div>
              <div className="col-md-4"><div className="card bg-white text-dark shadow-sm border-0" style={{borderRadius: '10px'}}><div className="card-body"><h6 className="card-title fw-bold text-muted mb-2 small text-uppercase">Ventas por Plataforma</h6><div style={{maxHeight: '80px', overflowY: 'auto'}}><ul className="list-unstyled small mb-0">{Object.entries(reportePlataformas).map(([nombre, datos]) => (<li key={nombre} className="d-flex justify-content-between mb-1 border-bottom pb-1"><span><strong>{nombre}</strong> <span className="text-muted">({datos.cantidad})</span></span><span className="text-success fw-bold">S/ {datos.dinero}</span></li>))}</ul></div></div></div></div>
          </div>
        </div>
        <div className="col-md-5 mb-4">
          <div className="card p-4 shadow-sm border-0 bg-white" style={{borderRadius: '10px'}} id="form-nuevo-cliente">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0" style={{color: '#5664d2'}}>
                {editandoCliente ? '✏️ EDITAR CLIENTE' : '+ NUEVO CLIENTE (30 DÍAS)'}
              </h5>
            </div>

            <form onSubmit={editandoCliente ? actualizarCliente : guardarCliente}>
              <h6 className="text-muted border-bottom pb-2 mb-3 small fw-bold">Datos del Cliente</h6>
              <div className="mb-3"><label className="form-label small text-muted">Nombre y apellidos:</label><input className="form-control" value={formCliente.nombre} onChange={e=>setFormCliente({...formCliente, nombre:e.target.value})} placeholder="" style={{height:'45px', backgroundColor:'#f8f9fa'}}/></div>
              <div className="mb-3"><label className="form-label small text-muted">Celular:</label><input className="form-control" value={formCliente.celular} onChange={e=>setFormCliente({...formCliente, celular:e.target.value})} placeholder="" style={{height:'45px', backgroundColor:'#f8f9fa'}}/></div>
              
              <h6 className="text-muted border-bottom pb-2 mt-4 mb-3 small fw-bold">Datos Suscripción</h6>
              <div className="row mb-2">
                <div className="col-6">
                    <label className="form-label small text-muted">Plataforma</label>
                    <select className="form-select" value={formCliente.servicio} onChange={e=>setFormCliente({...formCliente, servicio:e.target.value})} style={{height:'45px', backgroundColor:'#f8f9fa'}}>
                        {listaServiciosDisponibles.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <button type="button" className="btn btn-sm btn-outline-secondary mt-1" onClick={() => crearServicioPersonalizado('cliente')}>＋ Crear servicio</button>
                    {serviciosPersonalizados.some(servicio => servicio.toLowerCase() === formCliente.servicio.toLowerCase()) && <button type="button" className="btn btn-sm btn-outline-danger mt-1 ms-1" onClick={() => eliminarServicioPersonalizado(formCliente.servicio)}>Eliminar servicio</button>}
                </div>
                <div className="col-6"><label className="form-label small text-muted">Monto S/</label><input className="form-control" value={formCliente.monto} onChange={e=>setFormCliente({...formCliente, monto:e.target.value})} style={{height:'45px', backgroundColor:'#f8f9fa'}}/></div>
              </div>
              <div className="row mb-3"><div className="col-6"><label className="small fw-bold text-success">Fecha Inicio</label><input type="date" className="form-control" value={formCliente.fecha_inicio} onChange={handleFechaInicioChange} style={{borderColor: '#28a745'}}/></div><div className="col-6"><label className="small fw-bold text-danger">Vencimiento</label><input type="date" className="form-control bg-light" value={formCliente.fecha_fin} readOnly style={{borderColor: '#dc3545'}}/></div></div>
              
              {editandoCliente && (
                <>
                  <h6 className="text-muted border-bottom pb-2 mt-4 mb-3 small fw-bold">Ajustes de Suscripción</h6>
                  <div className="mb-3">
                    <label className="form-label small text-muted">Días Extras</label>
                    <input 
                      type="number" 
                      min="0" 
                      step="1" 
                      className="form-control" 
                      placeholder="Ej: 5" 
                      value={diasExtras} 
                      onChange={handleDiasExtrasChange} 
                      style={{height:'45px', backgroundColor:'#f8f9fa'}}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small text-muted">Comentarios de suscripción</label>
                    <textarea 
                      className="form-control" 
                      rows="3" 
                      placeholder="" 
                      value={comentarioAjuste} 
                      onChange={e => setComentarioAjuste(e.target.value)} 
                      style={{backgroundColor:'#f8f9fa'}}
                    />
                  </div>
                  {formCliente.fecha_fin && (
                    <div className="p-3 mb-3 rounded bg-light border" style={{fontSize: '0.875rem'}}>
                      <div className="text-muted">
                        <strong>Vencimiento actual:</strong> {formatearFechaLegible(formCliente.fecha_fin)}
                      </div>
                      <div className={(parseInt(diasExtras, 10) || 0) > 0 ? "text-success fw-bold mt-1" : "text-muted mt-1"}>
                        <strong>Nuevo vencimiento:</strong> {formatearFechaLegible(sumarDiasFechaCalendario(formCliente.fecha_fin, parseInt(diasExtras, 10) || 0))}
                        {(parseInt(diasExtras, 10) || 0) > 0 && (
                          <span className="badge bg-success ms-2">+{(parseInt(diasExtras, 10) || 0)} días</span>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
              
              <h6 className="text-muted border-bottom pb-2 mt-4 mb-3 small fw-bold">Cuenta Asignada</h6>
              <div className="row mb-2"><div className="col-6"><input className="form-control form-control-sm" placeholder="Correo" value={formCliente.correo} onChange={e=>setFormCliente({...formCliente, correo:e.target.value})}/></div><div className="col-6"><input className="form-control form-control-sm" placeholder="Contraseña" value={formCliente.contrasena} onChange={e=>setFormCliente({...formCliente, contrasena:e.target.value})}/></div></div>
              <div className="row mb-2"><div className="col-8"><input className="form-control form-control-sm" placeholder="Nombre Perfil" value={formCliente.perfil} onChange={e=>setFormCliente({...formCliente, perfil:e.target.value})}/></div><div className="col-4"><input className="form-control form-control-sm" placeholder="PIN" value={formCliente.pin} onChange={e=>setFormCliente({...formCliente, pin:e.target.value})}/></div></div>
              
              <button 
                type="submit"
                disabled={cargandoGuardar}
                className="btn w-100 fw-bold mt-3 py-2" 
                style={{
                  backgroundColor: editandoCliente ? '#ffc107' : '#00C853', 
                  color: editandoCliente ? '#000000' : 'white', 
                  borderRadius: '8px'
                }}
              >
                {cargandoGuardar ? 'GUARDANDO...' : (editandoCliente ? 'GUARDAR CAMBIOS' : 'REGISTRAR VENTA')}
              </button>
              {editandoCliente && (
                <button 
                  type="button" 
                  className="btn btn-secondary w-100 mt-2" 
                  onClick={limpiarFormCliente}
                >
                  Cancelar
                </button>
              )}
            </form>
          </div>
        </div>

        {/* COLUMNA DERECHA: CLIENTES AGRUPADOS */}
        <div className="col-md-7 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              {/* Título Gestión de Cuentas */}
              <h5 className="mb-0 fw-bold text-dark"><i className="bi bi-people-fill me-2"></i>Gestión de Cuentas</h5>

              {/* Buscador */}
              <div className="input-group" style={{maxWidth: '300px'}}>
                <span className="input-group-text bg-white border-end-0"><i className="bi bi-search text-muted"></i></span>
                <input 
                  type="text" 
                  className="form-control border-start-0 ps-0" 
                  placeholder="Buscar cliente, correo..." 
                  value={busquedaCliente} 
                  onChange={e => setBusquedaCliente(e.target.value)}
                  style={{height: '38px'}}
                />
                {busquedaCliente && (
                  <button className="btn btn-outline-secondary border-start-0" type="button" onClick={() => setBusquedaCliente('')}>
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>

              {/* Dropdown de Apps */}
              <div className="dropdown" ref={dropdownRef}>
                <button 
                  className="btn btn-dark dropdown-toggle rounded-pill fw-bold shadow-sm" 
                  type="button" 
                  onClick={() => setDropdownAbierto(prev => !prev)}
                  aria-expanded={dropdownAbierto} 
                  style={{fontSize:'0.85rem'}}
                >
                  <i className="bi bi-funnel-fill me-2"></i>{filtroCliente === 'Todos' || !filtroCliente ? 'Todas las Apps' : filtroCliente}
                </button>
                <ul className={`dropdown-menu dropdown-menu-end shadow ${dropdownAbierto ? 'show' : ''}`} style={{maxHeight: '350px', overflowY: 'auto'}}>
                  <li>
                    <button 
                      className={`dropdown-item ${(!filtroCliente || filtroCliente === 'Todos' || filtroCliente === '') ? 'active' : ''}`} 
                      onClick={() => { setFiltroCliente('Todos'); setDropdownAbierto(false); }}
                    >
                      <span style={{ color: '#000000', fontWeight: 700 }}>Todas las apps</span> ({safeClientes.length})
                    </button>
                  </li>
                  <li><hr className="dropdown-divider"/></li>
                  {listaServiciosDisponibles.map(plat => {
                    const count = safeClientes.filter(c => (c.servicio || c.service || '').toLowerCase() === plat.toLowerCase()).length;
                    return (
                      <li key={plat}>
                        <button 
                          className={`dropdown-item d-flex justify-content-between align-items-center ${filtroCliente === plat ? 'active' : ''}`} 
                          onClick={() => { setFiltroCliente(plat); setDropdownAbierto(false); }}
                        >
                          <span style={{ color: '#000000', fontWeight: 700 }}>{plat}</span>
                          <span className="badge bg-secondary ms-2">{count}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            <div style={{maxHeight:'800px', overflowY:'auto', paddingRight:'5px'}}>
              {serviciosOrdenados.length === 0 && (
                <div className="alert alert-secondary text-center">No hay clientes activos en esta categoría.</div>
              )}
              {serviciosOrdenados.map(servicio => {
                const cuentasDeEsteServicio = clientesAgrupados[servicio];
                const colorMarca = getBrandColor(servicio);

                const correosOrdenados = Object.keys(cuentasDeEsteServicio).sort((correoA, correoB) => {
                  const getMinDateEmail = (list) => {
                    let minTs = Infinity;
                    list.forEach(p => {
                      const d = getFechaVencimientoStr(p);
                      if (d) {
                        const ts = dayjs(d).valueOf();
                        if (ts < minTs) minTs = ts;
                      }
                    });
                    return minTs;
                  };
                  return getMinDateEmail(cuentasDeEsteServicio[correoA]) - getMinDateEmail(cuentasDeEsteServicio[correoB]);
                });

                return (
                  <div key={servicio} className="mb-4">
                    <div className="d-flex align-items-center mb-2">
                        <span className="badge rounded-pill me-2" style={{backgroundColor: colorMarca, fontSize:'0.9rem'}}>{servicio}</span>
                        <div style={{height:'1px', backgroundColor: colorMarca, flexGrow:1, opacity:0.3}}></div>
                    </div>
                    {correosOrdenados.map(correo => {
                      const clientesEnCuenta = cuentasDeEsteServicio[correo];
                      const clientesEnCuentaOrdenados = [...clientesEnCuenta].sort(compararVencimiento);

                      const stockEncontrado = dataInventario.find(i =>
                        i.correo.trim().toLowerCase() === correo.trim().toLowerCase() &&
                        (i.servicio || '').trim().toLowerCase() === (servicio || '').trim().toLowerCase()
                      );
                      const passwordCuenta = clientesEnCuenta.find(c => c.contrasena && c.contrasena !== '••••••••' && c.contrasena !== '********')?.contrasena
                        || (stockEncontrado && credencialesReveladas[stockEncontrado.id])
                        || (stockEncontrado && stockEncontrado.contrasena && stockEncontrado.contrasena !== '••••••••' && stockEncontrado.contrasena !== '********' ? stockEncontrado.contrasena : '')
                        || (clientesEnCuenta[0]?.contrasena && clientesEnCuenta[0]?.contrasena !== '••••••••' ? clientesEnCuenta[0]?.contrasena : '')
                        || (stockEncontrado && !credencialesReveladas[stockEncontrado.id] ? '...' : '-');

                      return (
                        <div key={correo} className="card shadow-sm border-0 mb-3" style={{overflow:'hidden', borderRadius:'10px'}}>
                          <div className="card-header bg-light border-0 d-flex justify-content-between align-items-center py-2">
                              <div
                                className="d-flex align-items-center gap-2 flex-wrap"
                                style={{ minWidth: 0 }}
                              >
                                <i className="bi bi-envelope-at-fill text-muted"></i>
                                  <div
                                    className="fw-bold text-dark"
                                    style={{
                                      maxWidth: '100%',
                                      whiteSpace: 'normal',
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word'
                                    }}
                                    title={correo}
                                  >
                                    {correo}
                                  </div>
                                <div className="badge bg-secondary bg-opacity-10 text-dark border ms-1">Pass: {passwordCuenta}</div>
                             </div>
                             <div className="d-flex align-items-center gap-2">
                                <button 
                                  type="button"
                                  onClick={() => handleGenerarPerfilLibreCuenta(servicio, correo, passwordCuenta, clientesEnCuenta, stockEncontrado?.id)}
                                  className="btn btn-sm text-white fw-bold px-2 py-1 shadow-sm"
                                  style={{ backgroundColor: '#00C853', borderRadius: '15px', fontSize: '0.75rem', border: 'none' }}
                                  title="Generar perfil"
                                >
                                  <i className="bi bi-plus-circle"></i>
                                </button>
                                <button
                                   type="button"
                                   onClick={() => handleActualizacionClave(servicio, correo, passwordCuenta)}
                                   className="btn btn-sm fw-bold px-2 py-1 shadow-sm"
                                   style={{ backgroundColor: '#6366f1', color: 'white', borderRadius: '15px', fontSize: '0.75rem', border: 'none' }}
                                   title="Actualización de clave"
                                 >
                                   🔑
                                 </button>
                                <span className="badge bg-primary rounded-pill">{clientesEnCuenta.length} Perfiles</span>
                             </div>
                          </div>
                          <div className="table-responsive">
                    <table className="table table-hover mb-0 align-middle">
                                <thead className="table-light"><tr><th className="ps-3">CLIENTE</th><th>PERFIL</th><th className="text-center">INICIO</th><th className="text-center">VENTA</th><th className="text-center">VENCE</th><th className="text-end pe-3">ACCIONES</th></tr></thead>
                                <tbody>
                                  {clientesEnCuentaOrdenados.map(c => {
                                     const fechaFinStr = getFechaVencimientoStr(c);
                                     const diasRestantes = fechaFinStr ? dayjs(fechaFinStr).diff(dayjs(), 'day') : 999;
                                     const celular = c.numero_celular || c.celular;
                                     const pinVal = (pinsRevelados[c.id] || c.pin_perfil || c.pin || '').toString().trim();

                                     return (
                                       <tr key={c.id}>
                                         <td className="ps-3" style={{width:'30%'}}>
                                            <div className="fw-bold text-dark small">{c.nombre_cliente || c.nombre}</div>
                                            { celular ? (
                                              <div className="text-success small" style={{fontSize:'0.7rem'}}>
                                                <i className="bi bi-whatsapp me-1"></i>{celular}
                                              </div>
                                            ) : (
                                              <div className="text-muted small opacity-75" style={{fontSize:'0.7rem'}}>
                                                <i className="bi bi-dash-circle me-1"></i>Sin celular
                                              </div>
                                            )}
                                         </td>
                                         <td>
                                            <div className="badge bg-light text-dark border">
                                              <i className="bi bi-person-circle me-1"></i>{c.perfil}
                                              {pinVal ? <span className="text-muted border-start ps-1 ms-1">{pinVal}</span> : null}
                                              {pinVal === '••••••••' && !pinsRevelados[c.id] ? (
                                                <button type="button" className="btn btn-sm btn-link p-0 ms-1" onClick={() => revelarPin(c.id)} title="Mostrar PIN durante 15 segundos">
                                                  <i className="bi bi-eye"></i>
                                                </button>
                                              ) : null}
                                            </div>
                                         </td>
                                         <td className="text-center"><span className="badge rounded-pill bg-light text-dark border" style={{fontSize:'0.75rem'}}>{formatearFechaDDMM(c.fecha_inicio || c.start_date)}</span></td>
                                         <td className="text-center"><span className="badge rounded-pill bg-success-subtle text-success border border-success-subtle" style={{fontSize:'0.75rem'}}>S/ {Number(c.monto || c.amount || 0).toFixed(2)}</span></td>
                                         <td className="text-center"><span className={`badge rounded-pill ${diasRestantes < 3 ? 'bg-danger' : 'bg-success'}`} style={{fontSize:'0.75rem'}}>{fechaFinStr ? dayjs(fechaFinStr).add(10,'hour').format('DD/MM') : '-'}</span></td>
                                         <td className="text-end pe-3">
                                            <button 
                                              type="button"
                                              onClick={() => handleAbrirModalRenovar(c)} 
                                              className="btn btn-sm text-white rounded-pill px-2 py-1 me-2 fw-bold shadow-sm d-inline-flex align-items-center gap-1 border-0"
                                              style={{ fontSize: '0.75rem', backgroundColor: '#10B981' }}
                                              title="Renovar este perfil (+30 días)"
                                            >
                                              <i className="bi bi-arrow-repeat"></i>
                                            </button>
                                            {!esPerfilLibre(c) && (
                                              <button 
                                                type="button" 
                                                onClick={() => handleEnviarCuenta(c, correo, servicio, stockEncontrado)} 
                                                className="btn btn-sm btn-link p-0 me-2 text-decoration-none" 
                                                title="Enviar cuenta"
                                              >
                                                📤
                                              </button>
                                            )}
                                            {!esPerfilLibre(c) && (
                                              <button
                                                type="button"
                                                onClick={() => handleRecordatorioRenovacion(c, servicio)}
                                                className="btn btn-sm btn-link p-0 me-2 text-decoration-none"
                                                title="Recordatorio de renovación"
                                              >
                                                🔔
                                              </button>
                                            )}
                                            <button onClick={() => handleEditarCliente(c)} className="btn btn-sm btn-link p-0 me-2 text-decoration-none" title="Editar">✏️</button>
                                            <button onClick={() => handleEliminar(c, 'cliente')} className="btn btn-sm btn-link p-0 text-decoration-none" title="Mover a Papelera">❌</button>
                                         </td>
                                       </tr>
                                     );
                                  })}
                                </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
        </div>
        
        {/* SECCIÓN INFERIOR: STOCK DE CUENTAS (INVENTARIO) */}
        <div className="col-12 mt-4 mb-4"><div className="d-flex align-items-center"><div className="flex-grow-1" style={{height:'2px', backgroundColor:'#ffc107'}}></div><h4 className="mx-4 fw-bold mb-0" style={{color: '#ffc107', letterSpacing:'1px'}}><i className="bi bi-key-fill me-2"></i>GESTIÓN DE STOCK (VACÍAS)</h4><div className="flex-grow-1" style={{height:'2px', backgroundColor:'#ffc107'}}></div></div></div>
        <div className="col-12"><CredentialDeliveryTasks api={api} key={credentialTasksVersion} /></div>
        
        {/* FORMULARIO STOCK */}
        <div className="col-md-5 mb-4">
            <div className="card p-4 shadow-sm border-0 bg-white" style={{borderRadius: '10px'}}>
                <h5 className="fw-bold mb-3" style={{color: '#ffc107'}}>{editandoStock ? '✏️ EDITAR STOCK' : '+ NUEVA CUENTA (STOCK)'}</h5>
                <form onSubmit={editandoStock ? actualizarCuenta : guardarCuenta}>
                    {!editandoStock && <div className="mb-3">
                      <label className="small text-muted">Tipo de registro</label>
                      <select className="form-select" value={formStock.mode} onChange={e => { setReplacementPreview(null); setFormStock({ ...formStock, mode: e.target.value, sourceAccountId: '', reason: '', note: '' }); }}>
                        <option value="new">Cuenta nueva</option>
                        <option value="replacement">Reemplazo / garantía</option>
                      </select>
                    </div>}
                    {formStock.mode === 'replacement' && !editandoStock && <div className="border rounded p-3 mb-3 bg-light">
                      <label className="small text-muted">Cuenta que será reemplazada</label>
                      <select className="form-select mb-2" value={formStock.sourceAccountId} onChange={async e => {
                        const sourceAccountId = e.target.value;
                        const source = safeInventario.find(account => String(account.id) === sourceAccountId);
                        setFormStock(current => ({ ...current, sourceAccountId, servicio: source?.servicio || source?.service || current.servicio }));
                        setReplacementPreview(null);
                        if (!sourceAccountId) return;
                        const authToken = localStorage.getItem('token') || sessionStorage.getItem('authToken') || '';
                        try {
                          const { data } = await axios.get(`${api}/inventario/${sourceAccountId}/replacement-preview`, { headers: { Authorization: `Bearer ${authToken}` } });
                          setReplacementPreview(data);
                          setFormStock(current => ({ ...current, capacidad: data.suggestedCapacity }));
                        } catch (_) {
                          mostrarToast('No se pudo consultar cuántos perfiles se trasladarán', 'danger');
                        }
                      }}>
                        <option value="">Selecciona una cuenta</option>
                        {safeInventario.map(account =>
                          <option key={account.id} value={account.id}>{account.correo} · {account.servicio || account.service}{account.status === 'failed' ? ' · caída' : ''}</option>
                        )}
                      </select>
                      {replacementPreview && <div className="small mb-2">Se trasladarán <strong>{replacementPreview.profilesToMove}</strong> perfiles vigentes.</div>}
                      <label className="small text-muted">Motivo</label>
                      <input className="form-control mb-2" maxLength={120} value={formStock.reason} onChange={e => setFormStock({ ...formStock, reason: e.target.value })} placeholder="Ej. garantía del proveedor" />
                      <label className="small text-muted">Nota adicional (opcional)</label>
                      <textarea className="form-control" maxLength={500} rows={2} value={formStock.note} onChange={e => setFormStock({ ...formStock, note: e.target.value })} />
                    </div>}
                    <div className="mb-2"><label className="small text-muted">Correo</label><input className="form-control" value={formStock.correo} onChange={e=>updateStockForm({ correo:e.target.value })} style={{height:'45px', backgroundColor:'#f8f9fa'}}/></div>
                    <div className="mb-2"><label htmlFor="stock-credential-input" className="small text-muted">Contraseña</label><input id="stock-credential-input" type="password" autoComplete="new-password" className="form-control" placeholder={editandoStock ? 'Dejar vacío para conservar la actual' : ''} value={formStock.contrasena} onChange={e=>updateStockForm({ contrasena:e.target.value })} style={{height:'45px', backgroundColor:'#f8f9fa'}}/></div>
                    <div className="mb-2">
                        <label className="small text-muted">Servicio</label>
                        <select className="form-select" value={formStock.servicio} disabled={formStock.mode === 'replacement'} onChange={e=>updateStockForm({ servicio:e.target.value })} style={{height:'45px', backgroundColor:'#f8f9fa'}}>
                            {listaServiciosDisponibles.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <button type="button" className="btn btn-sm btn-outline-secondary mt-1" onClick={() => crearServicioPersonalizado('stock')}>＋ Crear servicio</button>
                        {serviciosPersonalizados.some(servicio => servicio.toLowerCase() === formStock.servicio.toLowerCase()) && <button type="button" className="btn btn-sm btn-outline-danger mt-1 ms-1" onClick={() => eliminarServicioPersonalizado(formStock.servicio)}>Eliminar servicio</button>}
                    </div>
                    <div className="mb-2"><label className="small text-muted fw-bold">Costo (S/)</label><input type="number" onWheel={handleWheel} className="form-control" value={formStock.costo} onChange={e=>updateStockForm({ costo:e.target.value })} placeholder="Ej: 30" style={{height:'45px', backgroundColor:'#f8f9fa'}}/></div>
                    {formStock.mode === 'replacement' && <div className="mb-2"><label className="small text-muted fw-bold">Capacidad de la cuenta destino</label><input type="number" min="1" className="form-control" value={formStock.capacidad || 5} onChange={e=>setFormStock({...formStock, capacidad:e.target.value})}/></div>}
                    <div className="row mb-3"><div className="col-6"><label className="small fw-bold text-success">Entrada</label><input type="date" className="form-control" value={formStock.fecha_entrada} onChange={handleFechaStockChange} style={{borderColor: '#28a745'}}/></div><div className="col-6"><label className="small fw-bold text-danger">Vence</label><input type="date" className="form-control bg-light" value={formStock.fecha_vencimiento} readOnly style={{borderColor: '#dc3545'}}/></div></div>
                    <button className={`btn w-100 fw-bold mt-3 py-2`} style={{backgroundColor: editandoStock ? '#0d6efd' : '#ffc107', color: editandoStock?'white':'black', borderRadius:'8px'}}>{editandoStock ? 'ACTUALIZAR STOCK' : formStock.mode === 'replacement' ? 'REGISTRAR REEMPLAZO' : 'GUARDAR EN STOCK'}</button>{editandoStock && <button type="button" className="btn btn-secondary w-100 mt-2" onClick={limpiarFormStock}>Cancelar</button>}
                </form>
            </div>
        </div>

        {/* LISTA STOCK */}
        <div className="col-md-7">
          <div className="card shadow-sm border-0" style={{borderRadius: '12px', overflow: 'visible', position: 'relative'}}>
            
            <div className="p-3 bg-white border-bottom d-flex flex-wrap justify-content-between align-items-center gap-3" style={{borderTopLeftRadius: '12px', borderTopRightRadius: '12px', position: 'relative', zIndex: 5}}>
                <div className="d-flex align-items-center gap-2">
                  <h5 className="mb-0 text-dark">{filtroStock === 'Caidas' ? 'Cuentas caídas' : 'Stock Disponible'}</h5>
                  <span className="badge rounded-pill bg-light text-dark border">{stockFiltrado.length}</span>
                </div>
                
                <div className="dropdown position-relative">
                  <button className="btn btn-warning dropdown-toggle rounded-pill fw-bold shadow-sm text-dark" type="button" data-bs-toggle="dropdown" aria-expanded="false" style={{fontSize:'0.85rem', minHeight:'40px'}}>
                    <i className="bi bi-funnel-fill me-2"></i>{filtroStock === 'Todos' ? 'Todos los servicios' : filtroStock}
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end shadow" style={{maxHeight: 'min(50vh, 320px)', overflowY: 'auto', zIndex: 1080}}>
                    <li><button className={`dropdown-item ${filtroStock==='Todos'?'active':''}`} onClick={() => setFiltroStock('Todos')}>Todos ({dataInventario.filter(c => c.status !== 'failed').length})</button></li>
                    <li><button className={`dropdown-item ${filtroStock==='Caidas'?'active':''}`} onClick={() => setFiltroStock('Caidas')}>Caídas e historial ({dataInventario.filter(c => c.status === 'failed').length})</button></li>
                    <li><hr className="dropdown-divider"/></li>
                    {listaServiciosDisponibles.map(plat => (
                      <li key={plat}>
                        <button className={`dropdown-item d-flex justify-content-between ${filtroStock===plat?'active':''}`} onClick={() => setFiltroStock(plat)}>
                          {plat} <span className="badge bg-secondary ms-2">{conteoStock[plat] || 0}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
            </div>

            <div className="table-responsive" style={{borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px'}}>
              <table className="table table-hover mb-0 align-middle" style={{minWidth: '680px'}}>
                <thead className="text-white" style={{backgroundColor: '#ffc107', color:'black'}}><tr>
                <th className="py-3 ps-3 text-dark">CUENTA</th>
                <th className="py-3 text-dark">SERVICIO/PASS</th>
                <th className="py-3 text-center text-dark">COSTO</th>
                <th className="py-3 text-center text-dark">INICIO</th>
                <th className="py-3 text-center text-dark">VENCE</th>
                <th className="py-3 text-center text-dark">ACCIÓN</th>
              </tr></thead>
              <tbody>
                {stockFiltrado.map(i => (
                  <tr key={i.id} className="border-bottom">
                    <td className="fw-bold ps-3">{i.correo}<div>{i.status === 'failed' ? <span className="badge bg-danger mt-1">Caída</span> : i.originType === 'replacement' ? <span className="badge bg-info text-dark mt-1">Reemplazo</span> : i.originType === 'new' ? <span className="badge bg-success-subtle text-success mt-1">Nueva</span> : <span className="badge bg-secondary-subtle text-secondary mt-1">Sin clasificar</span>}</div>{i.status === 'failed' && <div>{i.failureReason && <div className="small text-danger mt-1">{i.failureReason}</div>}{i.replacementEmail && <div className="small text-muted mt-1">Reemplazada por {i.replacementEmail}</div>}</div>}</td>
                    <td>
                        <div>{i.servicio}</div>
                        <div className="d-flex align-items-center gap-1">
                          <span className="text-muted small">{credencialesReveladas[i.id] || i.contrasena}</span>
                          {!credencialesReveladas[i.id] && (
                            <button type="button" className="btn btn-sm btn-link p-0" onClick={() => revelarCredencial(i.id)} title="Mostrar durante 15 segundos">
                              <i className="bi bi-eye"></i>
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="text-center fw-bold text-secondary">S/ {i.costo || '0'}</td>
                      <td className="text-center"><span className="badge rounded-pill bg-secondary-subtle text-secondary">{formatearFechaDDMM(i.fecha_entrada)}</span></td>
                      <td className="text-center"><span className="badge rounded-pill" style={{backgroundColor: dayjs(i.fecha_vencimiento).add(10, 'hour').diff(dayjs(), 'day') < 5 ? '#dc3545' : '#198754'}}>{i.fecha_vencimiento ? dayjs(i.fecha_vencimiento).add(10, 'hour').format('DD/MM') : '-'}</span></td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2">
                          {i.status !== 'failed' && <button
                            onClick={() => handleAbrirModalRenovarStock(i)} 
                            className="btn btn-sm fw-bold shadow-sm d-inline-flex align-items-center gap-1" 
                            style={{backgroundColor:'#10B981', color:'white', border:'none', borderRadius:'20px', padding:'5px 14px'}}
                            title={`Renovar ${i.servicio} (${i.correo})`}
                          >
                            <i className="bi bi-arrow-repeat"></i> renovar
                          </button>}
                          {i.status === 'failed' ? <button onClick={() => cambiarEstadoCaidaStock(i, 'restore')} className="btn btn-sm btn-outline-success" title="Restaurar cuenta">Restaurar</button> : <button onClick={() => cambiarEstadoCaidaStock(i, 'report')} className="btn btn-sm btn-outline-danger" title="Reportar caída">Reportar caída</button>}
                          <button onClick={() => registrarCambioClaveExterno(i)} className="btn btn-sm btn-outline-primary" title="Registrar una clave cambiada en el proveedor">Clave externa</button>
                          <button onClick={() => mostrarHistorialCaidaStock(i)} className="btn btn-sm btn-link p-0" title="Ver historial">Historial</button>
                          {i.status !== 'failed' && <><button onClick={() => handleEditarStock(i)} className="btn btn-sm btn-link p-0" title="Editar">✏️</button>
                          <button onClick={() => handleEliminar(i, 'stock')} className="btn btn-sm btn-link p-0 text-danger" title="Eliminar"><i className="bi bi-trash-fill"></i></button></>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* 🗑️ MODAL DE PAPELERA DE PERFILES ELIMINADOS */}
        {mostrarPapelera && (
          <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 1060 }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                <div className="modal-header bg-danger text-white">
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-trash3-fill fs-5"></i>
                    <h5 className="modal-title fw-bold mb-0">Papelera de Perfiles Eliminados</h5>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close btn-close-white" 
                    onClick={() => { setMostrarPapelera(false); setSelectedTrashIds([]); }}
                  ></button>
                </div>
                <div className="modal-body p-4">
                  {/* Buscador */}
                  <div className="input-group mb-3 shadow-sm">
                    <span className="input-group-text bg-white"><i className="bi bi-search text-muted"></i></span>
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="Buscar en papelera por cliente, correo o servicio..." 
                      value={filtroPapelera}
                      onChange={e => setFiltroPapelera(e.target.value)}
                    />
                    {filtroPapelera && (
                      <button className="btn btn-outline-secondary" type="button" onClick={() => setFiltroPapelera('')}>
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                  </div>

                  {/* Acciones Masivas */}
                  {safeEliminados.length > 0 && (
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 pb-2 border-bottom">
                      <div className="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          className="btn btn-danger btn-sm rounded-pill px-3 fw-bold d-inline-flex align-items-center gap-1 shadow-sm"
                          onClick={handleBorrarSeleccionadosPapelera}
                          disabled={selectedTrashIds.length === 0 || isBulkDeleting}
                        >
                          {isBulkDeleting ? (
                            <>
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              <span>Eliminando...</span>
                            </>
                          ) : (
                            <>
                              <i className="bi bi-trash3-fill"></i>
                              <span>Borrar seleccionados{selectedTrashIds.length > 0 ? ` (${selectedTrashIds.length})` : ''}</span>
                            </>
                          )}
                        </button>

                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm rounded-pill px-3 fw-bold d-inline-flex align-items-center gap-1"
                          onClick={handleVaciarPapelera}
                          disabled={safeEliminados.length === 0 || isBulkDeleting}
                          title="Eliminar permanentemente todos los perfiles de la papelera"
                        >
                          <i className="bi bi-trash"></i>
                          <span>Vaciar papelera</span>
                        </button>
                      </div>

                      {selectedTrashIds.length > 0 && (
                        <span className="badge bg-danger bg-opacity-10 text-danger border border-danger-subtle px-3 py-2 rounded-pill small">
                          <i className="bi bi-check2-square me-1"></i>
                          {selectedTrashIds.length} seleccionado{selectedTrashIds.length > 1 ? 's' : ''} de {safeEliminados.length}
                        </span>
                      )}
                    </div>
                  )}

                  {safeEliminados.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bi bi-trash2 fs-1 d-block mb-2 text-secondary opacity-50"></i>
                      <h6>La papelera está vacía</h6>
                      <small>Los perfiles que elimines aparecerán aquí para ser restaurados.</small>
                    </div>
                  ) : perfilesPapeleraFiltrados.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <i className="bi bi-search fs-3 d-block mb-2 text-secondary opacity-50"></i>
                      <h6>No se encontraron resultados</h6>
                      <small>No hay perfiles en la papelera que coincidan con "{filtroPapelera}".</small>
                    </div>
                  ) : (
                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <table className="table table-hover align-middle">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th style={{ width: '40px' }} className="text-center">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                style={{ cursor: 'pointer' }}
                                checked={todosPapeleraSeleccionados}
                                ref={el => {
                                  if (el) el.indeterminate = algunosPapeleraSeleccionados;
                                }}
                                onChange={toggleSeleccionarTodosPapelera}
                                disabled={isBulkDeleting || idsPapeleraFiltrados.length === 0}
                                title="Seleccionar o deseleccionar todos los perfiles mostrados"
                              />
                            </th>
                            <th>Cliente / Servicio</th>
                            <th>Cuenta y Perfil</th>
                            <th className="text-center">Vencimiento</th>
                            <th>Monto</th>
                            <th className="text-end">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {perfilesPapeleraFiltrados.map(p => {
                            const isSelected = selectedTrashIds.includes(p.id);
                            const fechaVenceStr = getFechaVencimientoStr(p);
                            return (
                              <tr key={p.id} className={isSelected ? 'table-active' : ''}>
                                <td className="text-center">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    style={{ cursor: 'pointer' }}
                                    checked={isSelected}
                                    disabled={isBulkDeleting}
                                    onChange={() => toggleSeleccionarPerfilPapelera(p.id)}
                                  />
                                </td>
                                <td>
                                  <div className="fw-bold">{p.nombre_cliente || p.nombre || '>>> LIBRE <<<'}</div>
                                  <span className="badge rounded-pill me-1" style={{ backgroundColor: getBrandColor(p.servicio) }}>{p.servicio}</span>
                                  <small className="text-muted">{p.numero_celular || p.celular || 'Sin número'}</small>
                                </td>
                                <td>
                                  <div className="small text-muted">{p.correo || p.email}</div>
                                  <div className="badge bg-light text-dark border">{p.perfil}</div>
                                </td>
                                <td className="text-center">
                                  <span className="badge bg-light text-dark border fw-semibold" style={{ fontSize: '0.8rem' }}>
                                    {formatearFechaDDMM(fechaVenceStr)}
                                  </span>
                                </td>
                                <td className="fw-bold text-secondary">S/ {p.monto || '0'}</td>
                                <td className="text-end">
                                  <button 
                                    type="button"
                                    onClick={() => handleRestaurar(p.id, p.nombre_cliente || p.nombre)} 
                                    className="btn btn-sm btn-success rounded-pill px-3 fw-bold shadow-sm me-2"
                                    disabled={isBulkDeleting}
                                    title="Restaurar este perfil a la lista activa"
                                  >
                                    <i className="bi bi-arrow-counterclockwise me-1"></i>Restaurar
                                  </button>
                                  <button 
                                    type="button"
                                    onClick={() => handleEliminarDefinitivo(p.id, p.nombre_cliente || p.nombre)} 
                                    className="btn btn-sm btn-outline-danger rounded-pill px-2"
                                    disabled={isBulkDeleting}
                                    title="Eliminar permanentemente de la base de datos"
                                  >
                                    <i className="bi bi-x-circle"></i>
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="modal-footer bg-light">
                  <span className="text-muted small me-auto">Total eliminados: {safeEliminados.length}</span>
                  <button 
                    type="button" 
                    className="btn btn-secondary rounded-pill px-4" 
                    onClick={() => { setMostrarPapelera(false); setSelectedTrashIds([]); }}
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 🔄 MODAL DE RENOVACIÓN DE PERFIL */}
        {perfilRenovar && (
          <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 1065 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                <div className="modal-header bg-success text-white">
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-arrow-repeat fs-5"></i>
                    <h5 className="modal-title fw-bold mb-0">Renovar Perfil (+30 Días)</h5>
                  </div>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setPerfilRenovar(null)}></button>
                </div>

                <div className="modal-body p-4">
                  <div className="mb-3">
                    <label className="form-label small text-muted fw-bold">Cliente</label>
                    <input 
                      type="text" 
                      className="form-control fw-bold" 
                      value={formRenovar.nombre_cliente} 
                      onChange={e => setFormRenovar({...formRenovar, nombre_cliente: e.target.value})}
                    />
                  </div>

                  <div className="row mb-3">
                    <div className="col-6">
                      <label className="form-label small text-muted fw-bold">Plataforma</label>
                      <input type="text" className="form-control bg-light" value={formRenovar.servicio} readOnly />
                    </div>
                    <div className="col-6">
                      <label className="form-label small text-muted fw-bold">Perfil</label>
                      <input type="text" className="form-control bg-light" value={formRenovar.perfil} readOnly />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label small text-muted fw-bold">Monto a Cobrar (S/)</label>
                    <input 
                      type="number" 
                      step="0.10"
                      className="form-control fw-bold text-success" 
                      value={formRenovar.monto} 
                      onChange={e => setFormRenovar({...formRenovar, monto: e.target.value})}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="row mb-3">
                    <div className="col-6">
                      <label className="form-label small fw-bold text-primary">Fecha Inicio</label>
                      <input 
                        type="date" 
                        className="form-control" 
                        value={formRenovar.fecha_inicio} 
                        onChange={handleFechaInicioRenovarChange}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-bold text-success">Nuevo Vencimiento (+30d)</label>
                      <input 
                        type="date" 
                        className="form-control bg-light fw-bold text-success border-success" 
                        value={formRenovar.fecha_finalizacion} 
                        readOnly 
                      />
                    </div>
                  </div>
                </div>

                <div className="modal-footer bg-white border-top-0 d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-light rounded-pill px-4 fw-bold" onClick={() => setPerfilRenovar(null)} disabled={cargandoRenovacion}>
                    Cancelar
                  </button>
                  <button type="button" className="btn btn-success rounded-pill px-4 fw-bold shadow-sm d-flex align-items-center gap-2" onClick={ejecutarRenovacion} disabled={cargandoRenovacion}>
                    {cargandoRenovacion ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        <span>Renovando...</span>
                      </>
                    ) : (
                      <>
                        <i className="bi bi-arrow-repeat"></i>
                        <span>Confirmar Renovación</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 📦 MODAL DE RENOVACIÓN DE STOCK */}
        {stockRenovar && (
          <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 1070 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                <div className="modal-header text-white" style={{ backgroundColor: '#d97706' }}>
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-box-seam-fill fs-5"></i>
                    <h5 className="modal-title fw-bold mb-0">Renovar Cuenta de Stock</h5>
                  </div>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setStockRenovar(null)}></button>
                </div>

                <form onSubmit={handleConfirmarRenovacionStock}>
                  <div className="modal-body p-4">
                    <div className="mb-3">
                      <label className="form-label small text-muted fw-bold">Cuenta / Correo</label>
                      <input type="text" className="form-control bg-light" value={formRenovarStock.correo} readOnly />
                    </div>

                    <div className="row mb-3">
                      <div className="col-6">
                        <label className="form-label small text-muted fw-bold">Servicio</label>
                        <input type="text" className="form-control bg-light" value={formRenovarStock.servicio} readOnly />
                      </div>
                      <div className="col-6">
                        <label className="form-label small text-muted fw-bold">Costo / Inversión (S/)</label>
                        <input 
                          type="number" 
                          step="0.10"
                          className="form-control fw-bold text-dark" 
                          value={formRenovarStock.monto} 
                          onChange={e => setFormRenovarStock({...formRenovarStock, monto: e.target.value})}
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-6">
                        <label className="form-label small fw-bold text-primary">Fecha Inicio</label>
                        <input 
                          type="date" 
                          className="form-control" 
                          value={formRenovarStock.fechaInicio} 
                          onChange={handleFechaInicioRenovarStockChange}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label small fw-bold text-warning">Nuevo Vence (+30d)</label>
                        <input 
                          type="date" 
                          className="form-control bg-light fw-bold text-dark" 
                          value={formRenovarStock.nuevaFechaVence} 
                          readOnly 
                        />
                      </div>
                    </div>

                    <div className="form-check form-switch mt-3">
                      <input 
                        className="form-check-input" 
                        type="checkbox" 
                        id="checkCostoBase"
                        checked={formRenovarStock.actualizarCostoBase}
                        onChange={e => setFormRenovarStock({...formRenovarStock, actualizarCostoBase: e.target.checked})}
                      />
                      <label className="form-check-label small text-muted" htmlFor="checkCostoBase">
                        Actualizar costo base de esta cuenta en inventario
                      </label>
                    </div>
                  </div>

                  <div className="modal-footer bg-white border-top-0 d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-light rounded-pill px-4 fw-bold" onClick={() => setStockRenovar(null)} disabled={cargandoRenovacionStock}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn text-white rounded-pill px-4 fw-bold shadow-sm d-flex align-items-center gap-2" style={{ backgroundColor: '#d97706', border: 'none' }} disabled={cargandoRenovacionStock}>
                      {cargandoRenovacionStock ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          <span>Renovando...</span>
                        </>
                      ) : (
                        <>
                          <i className="bi bi-arrow-repeat"></i>
                          <span>Confirmar Renovación</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* 📤 MODAL ENVIAR CUENTA */}
        {modalEnviarCuenta && (
          <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 1075 }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px' }}>
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                <div className="modal-header bg-dark text-white py-3">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fs-5">📤</span>
                    <h6 className="modal-title fw-bold mb-0 text-uppercase">DATOS PARA ENVIAR</h6>
                  </div>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setModalEnviarCuenta(null)}></button>
                </div>
                <div className="modal-body p-3 bg-light">
                  <pre 
                    className="p-3 bg-white rounded border mb-0 text-dark" 
                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', fontSize: '0.9rem', lineHeight: '1.5' }}
                  >
                    {modalEnviarCuenta.plantilla}
                  </pre>
                </div>
                <div className="modal-footer bg-white border-top-0 d-flex justify-content-end gap-2 py-2 px-3">
                  <button type="button" className="btn btn-light rounded-pill px-3 fw-bold" onClick={() => setModalEnviarCuenta(null)}>
                    CERRAR
                  </button>
                  <button type="button" className="btn btn-success rounded-pill px-3 fw-bold shadow-sm d-inline-flex align-items-center gap-1" onClick={handleCopiarPlantilla}>
                    📋 COPIAR
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 🔑 MODAL ACTUALIZACIÓN DE CLAVE */}
        {modalActualizacionClave && (
          <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 1076 }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px' }}>
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                <div className="modal-header text-white py-3" style={{ backgroundColor: '#6366f1' }}>
                  <div className="d-flex align-items-center gap-2">
                    <span className="fs-5">🔑</span>
                    <h6 className="modal-title fw-bold mb-0 text-uppercase">Actualización de clave</h6>
                  </div>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setModalActualizacionClave(null)}></button>
                </div>
                <div className="modal-body p-3 bg-light">
                  <textarea
                    readOnly
                    rows={6}
                    className="form-control bg-white text-dark border"
                    style={{ whiteSpace: 'pre', fontFamily: 'inherit', fontSize: '0.9rem', lineHeight: '1.5', resize: 'none' }}
                    value={modalActualizacionClave.mensaje}
                  />
                </div>
                <div className="modal-footer bg-white border-top-0 d-flex justify-content-end gap-2 py-2 px-3">
                  <button type="button" className="btn btn-light rounded-pill px-3 fw-bold" onClick={() => setModalActualizacionClave(null)}>
                    CERRAR
                  </button>
                  <button type="button" className="btn rounded-pill px-3 fw-bold shadow-sm d-inline-flex align-items-center gap-1 text-white" style={{ backgroundColor: '#6366f1', border: 'none' }} onClick={handleCopiarActualizacionClave}>
                    📋 Copiar mensaje
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 🔔 MODAL RECORDATORIO DE RENOVACIÓN */}
        {modalRecordatorioRenovacion && (
          <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 1077 }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px' }}>
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                <div className="modal-header text-white py-3" style={{ backgroundColor: '#f59e0b' }}>
                  <div className="d-flex align-items-center gap-2">
                    <span className="fs-5">🔔</span>
                    <h6 className="modal-title fw-bold mb-0 text-uppercase">Recordatorio de Renovación</h6>
                  </div>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setModalRecordatorioRenovacion(null)}></button>
                </div>
                <div className="modal-body p-3 bg-light">
                  <textarea
                    readOnly
                    rows={13}
                    className="form-control bg-white text-dark border"
                    style={{ whiteSpace: 'pre', fontFamily: 'inherit', fontSize: '0.88rem', lineHeight: '1.5', resize: 'none' }}
                    value={modalRecordatorioRenovacion.mensaje}
                  />
                </div>
                <div className="modal-footer bg-white border-top-0 d-flex justify-content-end gap-2 py-2 px-3">
                  <button type="button" className="btn btn-light rounded-pill px-3 fw-bold" onClick={() => setModalRecordatorioRenovacion(null)}>
                    CERRAR
                  </button>
                  <button
                    type="button"
                    className="btn rounded-pill px-3 fw-bold shadow-sm d-inline-flex align-items-center gap-1 text-white"
                    style={{ backgroundColor: recordatorioCopiadoId ? '#16a34a' : '#f59e0b', border: 'none' }}
                    onClick={handleCopiarRecordatorio}
                  >
                    {recordatorioCopiadoId ? '✅ Mensaje copiado' : '📋 Copiar mensaje'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 🔔 TOAST DE NOTIFICACIONES */}
        {toast.mostrar && (
          <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 2000 }}>
            <div className={`toast show align-items-center text-white bg-${toast.tipo} border-0 shadow-lg`} role="alert" aria-live="assertive" aria-atomic="true">
              <div className="d-flex">
                <div className="toast-body fw-bold">
                  {toast.mensaje}
                </div>
                <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setToast({ mostrar: false, mensaje: '', tipo: 'success' })}></button>
              </div>
            </div>
          </div>
        )}

    </div>
  );
}

export default VistaStreaming;
